import { API_URL, supabase } from '../lib/supabase'
import { config } from '../config'

// --- Types ---

export interface Tender {
    id?: string;
    code: string;
    station: string;
    division?: string;
    contractor: string;
    period?: string;
    start_date?: string;
    end_date?: string;
    shifts: string[];
    weightage?: string;
    evaluationScale?: string;
    evidencePolicy?: string;
    // Phase 2 fields
    submissionLockTime?: string;
    evaluationLockTime?: string;
    inspectionLockTime?: string;
    manpower?: { id?: string; category: string; requiredCount: number }[];
    equipment?: { id?: string; name: string; requiredCount: number }[];
    evidence?: { id?: string; label: string; type: 'image' | 'document'; required: boolean; isLocked?: boolean }[];
    lockConfig?: { submissionTime?: string; lockTime?: string; shiftEndTimes?: Record<string, string>; lockDelayHours?: number };
    evaluationConfig?: { scoringScale: string; minPassingScore: number };
    status: 'Draft' | 'Active' | 'Live' | 'Upcoming' | 'Expired' | 'draft' | 'active' | 'completed' | 'cancelled';
    // SLA / Evaluation Config
    serviceConfig?: {
        id?: string;
        service_name?: string;
        serviceName?: string;
        area_name?: string;
        areaName?: string;
        cycle_type?: 'Daily' | 'Weekly' | 'Monthly';
        cycleType?: 'Daily' | 'Weekly' | 'Monthly';
        frequency: number;
        max_score_shift_a?: number;
        max_score_shift_b?: number;
        max_score_shift_c?: number;
        shifts?: { shift: string; maxScore: number }[];
        station_asset_id?: string;
        stationAssetId?: string;
    }[];
}

/** Tenders eligible for daily work log / submissions (not setup-only or closed). */
export function isTenderSelectableForWorkLog (t: Tender): boolean {
    const s = (t.status ?? '').toString().trim().toLowerCase()
    if (!s) return false
    return s === 'active' || s === 'live'
}

export interface Submission {
    id: string;
    tender_id?: string;
    tenderCode?: string;
    date?: string;
    submission_date?: string;
    shift: string;
    contractor?: string;
    manpower?: string;
    manpower_data?: unknown[];
    service_entries?: unknown[];
    evidenceFiles?: number;
    status:
        | 'Draft'
        | 'Submitted'
        | 'Evaluated'
        | 'Inspected'
        | 'Locked'
        | 'Pending'
        | 'draft'
        | 'submitted'
        | 'contractor_submitted'
        | 'evaluated'
        | 'inspected'
        | 'disputed'
        | 'finalized';
    workAreas?: unknown[];
    equipmentStatus?: string;
    equipment_status?: string;
    equipmentNotes?: string;
    equipment_notes?: string;
    supervisorRemarks?: string;
    supervisor_remarks?: string;
    issuesReported?: string;
    issues_reported?: string;
    evidenceList?: unknown[];
    submittedAt?: string;
    submitted_at?: string;
    submitted_by?: string | null;
    score?: number;
    total_present?: number;
    total_required?: number;
    attendance_percent?: number;
}

export interface BuyerEvaluation {
    id: string;
    submissionId: string;
    evaluator: string;
    scores: { area: string; score: number }[];
    dailyScore: number;
    remarks: string;
    submittedAt: string;
}

export interface InspectionFeedback {
    id: string;
    submissionId: string;
    inspector: string;
    counts: { excellent: number; ok: number; poor: number };
    score: number;
    remarks: string;
    submittedAt: string;
}

export interface Notification {
    id: string;
    user_id?: string;
    role?: string;
    tender_code?: string;
    type: string;
    title: string;
    body?: string;
    link?: string;
    severity?: 'info' | 'warning' | 'critical';
    status: 'unread' | 'read' | 'archived';
    metadata?: Record<string, unknown>;
    created_at?: string;
    read_at?: string;
}

/**
 * The TenderForm wizard collects LOA fields in camelCase (areaSqm,
 * unitRateInr, …). The backend scheduler (services/scheduler.ts) reads
 * `line.area_sqm`, etc. — so the JSONB column must be snake_case.
 * Translate at the API boundary; preserve everything else as-is so
 * existing fields (serviceName, frequency, …) keep working.
 */
function normaliseServiceLineToSnakeCase(raw: any): any {
    if (!raw || typeof raw !== 'object') return raw
    const out: any = { ...raw }
    const map: Record<string, string> = {
        stationAssetId: 'station_asset_id',
        areaSqm: 'area_sqm',
        unitRateInr: 'unit_rate_inr',
        workingDaysPerMonth: 'working_days_per_month',
        totalCyclesContract: 'total_cycles_contract',
        floorType: 'floor_type',
        typeOfArea: 'type_of_area',
    }
    for (const [cc, sc] of Object.entries(map)) {
        if (out[cc] !== undefined) {
            out[sc] = out[cc]
        }
    }
    // Bool toggles → addons string array (scheduler reads `line.addons`).
    const addons: string[] = Array.isArray(out.addons) ? [...out.addons] : []
    if (out.consumablesAddon && !addons.includes('consumables')) addons.push('consumables')
    if (out.machineryAddon && !addons.includes('machinery')) addons.push('machinery')
    if (addons.length > 0) out.addons = addons
    return out
}

// --- Service ---

class ApiService {
    // Fallback in-memory store
    private _mockTenders: Tender[] = [];
    private _mockSubmissions: Submission[] = [];
    private useBackend = true;

    // Normalize DB payload -> frontend Tender shape
    private normalizeTender(raw: Tender | (Partial<Tender> & Record<string, unknown>)): Tender {
        type RawTender = Partial<Tender> & Record<string, unknown>
        type ShiftCfg = { shift: string; maxScore: number }
        type ServiceRaw = {
            id?: string
            serviceName?: string
            service_name?: string
            name?: string
            areaName?: string
            area_name?: string
            cycleType?: 'Daily' | 'Weekly' | 'Monthly'
            cycle_type?: string
            frequency?: number | string
            cycles?: number
            shifts?: ShiftCfg[]
            max_score?: number
            max_score_shift_a?: number
            max_score_shift_b?: number
            max_score_shift_c?: number
            station_asset_id?: string
            stationAssetId?: string
        }

        const normServiceCycleType = (svc: ServiceRaw): 'Daily' | 'Weekly' | 'Monthly' => {
            const c = svc.cycleType || svc.cycle_type
            if (c === 'Daily' || c === 'Weekly' || c === 'Monthly') return c
            if (typeof c === 'string') {
                const lc = c.toLowerCase()
                if (lc === 'daily') return 'Daily'
                if (lc === 'weekly') return 'Weekly'
                if (lc === 'monthly') return 'Monthly'
            }
            const f = svc.frequency
            if (typeof f === 'string') {
                const lc = f.toLowerCase()
                if (lc === 'daily') return 'Daily'
                if (lc === 'weekly') return 'Weekly'
                if (lc === 'monthly') return 'Monthly'
            }
            return 'Daily'
        }

        const source = (raw as RawTender)
        const rawServices = source.serviceConfig || source.service_config || []
        const serviceList = Array.isArray(rawServices) ? (rawServices as ServiceRaw[]) : []
        const serviceConfig = serviceList.map((svc) => {
            let shifts: ShiftCfg[] = Array.isArray(svc.shifts) ? svc.shifts : [
                svc.max_score_shift_a !== undefined ? { shift: 'A', maxScore: svc.max_score_shift_a } : null,
                svc.max_score_shift_b !== undefined ? { shift: 'B', maxScore: svc.max_score_shift_b } : null,
                svc.max_score_shift_c !== undefined ? { shift: 'C', maxScore: svc.max_score_shift_c } : null
            ].filter(Boolean) as ShiftCfg[]
            if (shifts.length === 0 && typeof svc.max_score === 'number') {
                const m = svc.max_score
                shifts = ['A', 'B', 'C'].map(s => ({ shift: s, maxScore: m }))
            }

            const cycleType = normServiceCycleType(svc)
            let frequency = 1
            if (typeof svc.frequency === 'number') frequency = svc.frequency
            else if (typeof svc.cycles === 'number') frequency = svc.cycles
            else if (typeof svc.frequency === 'string') frequency = 1

            return {
                ...svc,
                id: svc.id,
                serviceName: svc.serviceName || svc.service_name || svc.name,
                areaName: svc.areaName || svc.area_name || 'General',
                stationAssetId: svc.stationAssetId || svc.station_asset_id,
                cycleType,
                frequency: frequency > 0 ? frequency : 1,
                shifts
            }
        })

        const asArray = <T,>(value: unknown): T[] => (Array.isArray(value) ? value as T[] : [])
        const manpower = asArray(source.manpower || source.manpower_config) as Tender['manpower']
        const equipment = asArray(source.equipment || source.equipment_config) as Tender['equipment']
        const evidence = asArray(source.evidence || source.evidence_config) as Tender['evidence']
        const lockConfig = (source.lockConfig || source.lock_config) as Tender['lockConfig']
        const evaluationConfig = (source.evaluationConfig || source.evaluation_config) as Tender['evaluationConfig']

        const statusRaw = source.status
        const statusNormalized =
            statusRaw != null && String(statusRaw).trim() !== ''
                ? String(statusRaw).trim()
                : undefined

        return {
            ...raw,
            id: (source.id as string | undefined) ?? (raw as Tender).id,
            code: String(source.code ?? (raw as Tender).code ?? ''),
            station: String(source.station ?? (raw as Tender).station ?? ''),
            contractor: String(source.contractor ?? (raw as Tender).contractor ?? ''),
            shifts: Array.isArray(source.shifts) ? source.shifts as string[] : ((raw as Tender).shifts || []),
            ...(statusNormalized !== undefined ? { status: statusNormalized as Tender['status'] } : {}),
            serviceConfig,
            manpower,
            equipment,
            evidence,
            lockConfig,
            evaluationConfig
        } as Tender
    }

    /** Normalize one tender; never throw — bad rows fall back to a minimal shape. */
    private safeNormalizeTender(raw: Tender | (Partial<Tender> & Record<string, unknown>)): Tender {
        try {
            return this.normalizeTender(raw)
        } catch (err) {
            console.error('[API] normalizeTender failed for', (raw as { code?: string })?.code || (raw as { id?: string })?.id, err)
            const source = raw as Record<string, unknown>
            return {
                id: typeof source.id === 'string' ? source.id : undefined,
                code: String(source.code ?? 'UNKNOWN'),
                station: String(source.station ?? ''),
                contractor: String(source.contractor ?? ''),
                shifts: Array.isArray(source.shifts) ? source.shifts as string[] : [],
                status: (String(source.status ?? 'active') as Tender['status']),
                serviceConfig: [],
            }
        }
    }

    constructor() {
        // Mock in-memory data is populated only in dev when VITE_USE_MOCK_API=true
        // (see top-level init after class — production excludes mockData chunk).
    }

    /**
     * Dev + VITE_USE_MOCK_API: seeds in-memory store from `mockData` (async-loaded chunk).
     */
    initMockStoreForDev (tenders: Tender[], submissions: Submission[]): void {
        this._mockTenders = tenders
        this._mockSubmissions = submissions
    }

    // Cached auth token to reduce getSession() calls (avoids 429 under rate limit)
    private _tokenCache: { token: string | null; expiresAt: number } | null = null
    private static readonly TOKEN_CACHE_MS = 60_000 // fallback if JWT has no exp
    /** Admin Login As — scopes list APIs to this user id via X-Impersonate-User-Id. */
    private _impersonateUserId: string | null = null

    setImpersonationUserId (userId: string | null): void {
        this._impersonateUserId = userId && String(userId).trim() ? String(userId).trim() : null
    }

    private decodeJwtPayloadSegment(segment: string): string | null {
        let b64 = segment.replace(/-/g, '+').replace(/_/g, '/')
        const pad = b64.length % 4
        if (pad === 2) b64 += '=='
        else if (pad === 3) b64 += '='
        else if (pad !== 0) return null
        try {
            return atob(b64)
        } catch {
            return null
        }
    }

    private parseJwtExpiryMs(token: string): number | null {
        try {
            const parts = token.split('.')
            if (parts.length < 2) return null
            const json = this.decodeJwtPayloadSegment(parts[1])
            if (!json) return null
            const payload = JSON.parse(json) as { exp?: number }
            return typeof payload.exp === 'number' ? payload.exp * 1000 : null
        } catch {
            return null
        }
    }

    private cacheExpiryForToken(token: string, now: number): number {
        const expMs = this.parseJwtExpiryMs(token)
        if (expMs && expMs > now) {
            // Refresh ~15s before exp, cap to 55m
            const untilExp = expMs - now - 15_000
            return now + Math.max(5_000, Math.min(untilExp, 55 * 60_000))
        }
        return now + ApiService.TOKEN_CACHE_MS
    }

    private async getAuthToken(): Promise<string | null> {
        const now = Date.now()
        if (this._tokenCache && this._tokenCache.expiresAt > now && this._tokenCache.token) {
            return this._tokenCache.token
        }

        // Prefer a server-validated user over getSession() alone — local storage can
        // hold a JWT that looks fine but fails signature/session checks on the API.
        const { data: userData, error: userError } = await supabase.auth.getUser()
        if (!userError && userData.user) {
            const { data: { session } } = await supabase.auth.getSession()
            if (session?.access_token) {
                this._tokenCache = {
                    token: session.access_token,
                    expiresAt: this.cacheExpiryForToken(session.access_token, now)
                }
                return session.access_token
            }
        }

        const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession()
        if (refreshError || !refreshData.session?.access_token) {
            this._tokenCache = null
            if (refreshError) {
                console.error('[API] Session refresh failed:', refreshError.message)
            }
            return null
        }

        const token = refreshData.session.access_token
        this._tokenCache = { token, expiresAt: this.cacheExpiryForToken(token, now) }
        return token
    }

    /**
     * Authenticated fetch that refreshes the session once on 401 Invalid token.
     */
    private async fetchWithAuth (
        url: string,
        init: RequestInit = {}
    ): Promise<Response> {
        const token = await this.getAuthToken()
        if (!token) {
            throw new Error('Not authenticated — please sign in again')
        }

        const headers = new Headers(init.headers || {})
        if (!headers.has('Authorization')) {
            headers.set('Authorization', `Bearer ${token}`)
        }
        if (this._impersonateUserId && !headers.has('X-Impersonate-User-Id')) {
            headers.set('X-Impersonate-User-Id', this._impersonateUserId)
        }

        let res = await fetch(url, { ...init, headers })
        if (res.status !== 401) return res

        // Stale/invalid access token — force refresh and retry once
        this._tokenCache = null
        const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession()
        if (refreshError || !refreshData.session?.access_token) {
            return res
        }
        const fresh = refreshData.session.access_token
        this._tokenCache = {
            token: fresh,
            expiresAt: this.cacheExpiryForToken(fresh, Date.now())
        }
        headers.set('Authorization', `Bearer ${fresh}`)
        if (this._impersonateUserId) {
            headers.set('X-Impersonate-User-Id', this._impersonateUserId)
        }
        res = await fetch(url, { ...init, headers })
        return res
    }

    /** Clear token cache (e.g. on logout). Call from AuthContext when signing out. */
    clearAuthCache(): void {
        this._tokenCache = null
    }

    /** Public accessor for modules that need a bearer token (e.g. offline queue). */
    async getAccessToken(): Promise<string | null> {
        return this.getAuthToken()
    }

    // Get auth headers from Supabase session
    private async getAuthHeaders(): Promise<HeadersInit> {
        const token = await this.getAuthToken()
        return {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
            ...(this._impersonateUserId ? { 'X-Impersonate-User-Id': this._impersonateUserId } : {}),
        }
    }

    /** Paths like `/supplies/items` or `/api/supplies/items` → single `/api/...` URL */
    private normalizeGenericApiPath(path: string): string {
        let p = path.startsWith('/') ? path : `/${path}`
        if (p.startsWith('/api/')) {
            p = p.slice(4)
        }
        return p
    }

    // --- Tenders ---

    async getTenders (params?: { status?: string }): Promise<Tender[]> {
        const allowMockFallback =
            config.useMockApi

        const query =
            params?.status && params.status !== 'all'
                ? `?status=${encodeURIComponent(params.status)}`
                : ''

        if (this.useBackend) {
            try {
                const res = await this.fetchWithAuth(`${API_URL}/api/tenders${query}`)
                const json = await res.json()
                if (!res.ok) {
                    throw new Error(json.error || `Failed to load tenders (${res.status})`)
                }
                if (json.success && Array.isArray(json.data)) {
                    let list = json.data.map((t: Partial<Tender> & Record<string, unknown>) =>
                        this.safeNormalizeTender(t)
                    )
                    if (params?.status === 'active') {
                        list = list.filter(isTenderSelectableForWorkLog)
                    }
                    return list
                }
                // Some gateways wrap the array; accept that shape too.
                if (json.success && json.data && Array.isArray(json.data.tenders)) {
                    return json.data.tenders.map((t: Partial<Tender> & Record<string, unknown>) =>
                        this.safeNormalizeTender(t)
                    )
                }
                throw new Error(json.error || 'Failed to load tenders')
            } catch (e) {
                console.error('[API] getTenders failed:', e)
                if (allowMockFallback) {
                    let list = [...this._mockTenders].map(t => this.safeNormalizeTender(t))
                    if (params?.status === 'active') {
                        list = list.filter(isTenderSelectableForWorkLog)
                    }
                    return list
                }
                throw e instanceof Error ? e : new Error('Failed to load tenders')
            }
        }
        let list = [...this._mockTenders].map(t => this.safeNormalizeTender(t))
        if (params?.status === 'active') {
            list = list.filter(isTenderSelectableForWorkLog)
        }
        return list
    }

    async createTender(tender: Partial<Tender>): Promise<Tender> {
        if (this.useBackend) {
            try {
                const headers = await this.getAuthHeaders()
                const res = await fetch(`${API_URL}/api/tenders`, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({
                        code: tender.code,
                        title: tender.station || tender.code,
                        contractor: tender.contractor,
                        station: tender.station,
                        startDate: tender.start_date || new Date().toISOString().split('T')[0],
                        endDate: tender.end_date || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                        shifts: tender.shifts || ['A', 'B', 'C'],
                    serviceConfig: (tender.serviceConfig || []).map(normaliseServiceLineToSnakeCase),
                    manpowerConfig: tender.manpower || [],
                    equipmentConfig: tender.equipment || [],
                    evidenceConfig: tender.evidence || [],
                    lockConfig: tender.lockConfig || {},
                    evaluationConfig: tender.evaluationConfig || {}
                    })
                })
                const json = await res.json()
                if (json.success && json.data) {
                return this.normalizeTender(json.data)
                }
            } catch (e) {
                console.error('Create tender API error:', e)
            }
        }
        // Fallback to mock
        const newTender: Tender = {
            ...tender,
            status: 'Draft'
        } as Tender
        this._mockTenders.push(newTender)
        return this.normalizeTender(newTender)
    }

    async updateTender(tender: Tender): Promise<Tender | undefined> {
        let tenderId = tender.id
        if (!tenderId && tender.code) {
            const full = await this.getTender(tender.code)
            tenderId = full?.id
        }
        if (!tenderId) {
            console.error('updateTender requires tender.id or tender.code')
            return undefined
        }
        if (this.useBackend) {
            try {
                const headers = await this.getAuthHeaders()
                const res = await fetch(`${API_URL}/api/tenders/${tenderId}`, {
                    method: 'PATCH',
                    headers,
                    body: JSON.stringify({
                        code: tender.code,
                        title: tender.station || tender.code,
                        contractor: tender.contractor,
                        station: tender.station,
                        start_date: tender.start_date,
                        end_date: tender.end_date,
                        status: tender.status,
                        shifts: tender.shifts,
                        service_config: (tender.serviceConfig || []).map(normaliseServiceLineToSnakeCase),
                        manpower_config: tender.manpower || [],
                        equipment_config: tender.equipment || [],
                        evidence_config: tender.evidence || [],
                        lock_config: tender.lockConfig || {},
                        evaluation_config: tender.evaluationConfig || {}
                    })
                })
                const json = await res.json()
                if (!res.ok) {
                    throw new Error(json.error || `Update failed (${res.status})`)
                }
                if (json.success && json.data) {
                    return this.normalizeTender(json.data)
                }
            } catch (e) {
                console.error('Update tender API error:', e)
                throw e
            }
        }
        // Mock update
        const idx = this._mockTenders.findIndex(t => t.id === tender.id || t.code === tender.code)
        if (idx >= 0) {
            this._mockTenders[idx] = { ...this._mockTenders[idx], ...tender }
            return this.normalizeTender(this._mockTenders[idx])
        }
        return undefined
    }

    async getTender(code: string): Promise<Tender | undefined> {
        if (this.useBackend) {
            try {
                const headers = await this.getAuthHeaders()
                const res = await fetch(`${API_URL}/api/tenders/${encodeURIComponent(code)}`, { headers })
                const json = await res.json()
                if (json.success && json.data) {
                    return this.normalizeTender(json.data as Partial<Tender> & Record<string, unknown>)
                }
            } catch (e) {
                console.error('Get tender API error:', e)
            }
        }
        const found = this._mockTenders.find(t => t.code === code)
        return found ? this.normalizeTender(found) : undefined
    }

    async deleteTender(id: string, code?: string): Promise<boolean> {
        if (this.useBackend) {
            try {
                const headers = await this.getAuthHeaders()
                // Use ID if available, otherwise code could be supported by backend if implemented
                const res = await fetch(`${API_URL}/api/tenders/${id}`, {
                    method: 'DELETE',
                    headers
                })
                const json = await res.json()
                return json.success
            } catch (e) {
                console.error('Delete tender API error:', e)
                return false
            }
        }
        // Mock delete
        this._mockTenders = this._mockTenders.filter(t => t.code !== code && t.id !== id)
        return true
    }

    // --- Submissions ---

    async getSubmissions(tenderCode: string): Promise<Submission[]> {
        if (this.useBackend) {
            try {
                const headers = await this.getAuthHeaders()
                const res = await fetch(`${API_URL}/api/submissions?tender_code=${tenderCode}`, { headers })
                const json = await res.json()
                if (json.success && json.data) {
                    return json.data
                }
            } catch (e) {
                console.error('Get submissions API error:', e)
            }
        }
        return this._mockSubmissions.filter(s => s.tenderCode === tenderCode)
    }

    /**
     * Get all submissions for a given date (across tenders). Admin use.
     */
    async getSubmissionsForDate(date: string): Promise<Submission[]> {
        if (this.useBackend) {
            try {
                const headers = await this.getAuthHeaders()
                const res = await fetch(`${API_URL}/api/submissions?date=${date}`, { headers })
                const json = await res.json()
                if (json.success && json.data) {
                    return json.data
                }
            } catch (e) {
                console.error('Get submissions for date API error:', e)
            }
        }
        return []
    }

    /**
     * Get existing draft for a specific tender/shift/date combination
     */
    async getDraft(params: {
        tenderCode: string
        shift: string
        date: string
    }): Promise<Submission | null> {
        if (this.useBackend) {
            try {
                const headers = await this.getAuthHeaders()
                const queryParams = new URLSearchParams({
                    tender_code: params.tenderCode,
                    shift: params.shift,
                    date: params.date
                })
                const res = await fetch(`${API_URL}/api/submissions/draft?${queryParams}`, { headers })
                const json = await res.json()
                if (json.success && json.data) {
                    return json.data
                }
            } catch (e) {
                console.error('Get draft API error:', e)
            }
        }
        return null
    }

    /**
     * Save draft submission (auto-save on changes)
     */
    async saveDraft(data: {
        tender_code: string
        tender_id?: string
        shift: string
        submission_date: string
        manpower_data?: Array<{ category: string; required: number; present: number }>
        service_entries?: Array<{
            id: string
            serviceName: string
            areaName: string
            cycleType: string
            cyclesLogged: Array<{
                cycleNumber: number
                timestamp: string
                photos: unknown[]
                notes: string
            }>
            status: string
        }>
        total_present?: number
        total_required?: number
        attendance_percent?: number
        supervisor_remarks?: string
        issues_reported?: string
        equipment_status?: string
        equipment_notes?: string
        evidence_list?: Array<{
            id: string
            name: string
            type: string
            url: string
            fileName: string
            size?: number
        }>
    }): Promise<Submission | null> {
        if (this.useBackend) {
            try {
                const headers = await this.getAuthHeaders()
                const res = await fetch(`${API_URL}/api/submissions`, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({
                        ...data,
                        submit: false // Always save as draft
                    })
                })
                const json = await res.json()
                if (json.success && json.data) {
                    return json.data
                }
            } catch (e) {
                console.error('Save draft API error:', e)
            }
        }
        return null
    }

    async createSubmission(data: Partial<Submission> & { tender_code?: string; tenderCode?: string; tenderId?: string; evidenceList?: any[]; evidence_list?: any[] }): Promise<Submission> {
        if (this.useBackend) {
            try {
                const headers = await this.getAuthHeaders()
                const requestBody: any = {
                        tender_id: data.tender_id || data.tenderId,
                        tender_code: data.tender_code || data.tenderCode,
                        shift: data.shift,
                        submission_date: data.submission_date || data.date || new Date().toISOString().split('T')[0],
                        manpower_data: data.manpower_data || [],
                        service_entries: data.service_entries || data.workAreas || [],
                        total_present: data.total_present || 0,
                        total_required: data.total_required || 0,
                        attendance_percent: data.attendance_percent || 0,
                        supervisor_remarks: data.supervisor_remarks || data.supervisorRemarks || '',
                        issues_reported: data.issues_reported || data.issuesReported || '',
                        equipment_status: data.equipment_status || data.equipmentStatus || 'operational',
                        equipment_notes: data.equipment_notes || data.equipmentNotes || '',
                        submit: data.status === 'Submitted' || data.status === 'submitted'
                }
                
                // Include evidence_list if provided
                if (data.evidenceList || data.evidence_list) {
                    requestBody.evidenceList = data.evidenceList || data.evidence_list
                    requestBody.evidence_list = data.evidenceList || data.evidence_list
                }
                
                const res = await fetch(`${API_URL}/api/submissions`, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify(requestBody)
                })
                const json = await res.json()
                if (json.success && json.data) {
                    return json.data
                }
            } catch (e) {
                console.error('Create submission API error:', e)
            }
        }
        // Fallback
        const newSubmission = {
            id: `new-${Date.now()}`,
            status: 'Submitted',
            date: new Date().toLocaleDateString(),
            evidenceFiles: 0,
            ...data
        } as Submission
        this._mockSubmissions.push(newSubmission)
        return newSubmission
    }

    async updateSubmission(id: string, updates: Partial<Submission>): Promise<Submission> {
        // Find and update mock
        const index = this._mockSubmissions.findIndex(s => s.id === id)
        if (index !== -1) {
            this._mockSubmissions[index] = { ...this._mockSubmissions[index], ...updates }
            return this._mockSubmissions[index]
        }
        throw new Error('Submission not found')
    }

    // --- Evaluation ---

    async submitEvaluation(submissionId: string, evaluation: Partial<BuyerEvaluation>): Promise<void> {
        if (this.useBackend) {
            try {
                const headers = await this.getAuthHeaders()
                await fetch(`${API_URL}/api/evaluations`, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({
                        submission_id: submissionId,
                        scores_data: evaluation.scores,
                        remarks: evaluation.remarks
                    })
                })
            } catch (e) {
                console.error('Submit evaluation API error:', e)
            }
        }
    }

    // --- Inspection ---

    async submitInspection(submissionId: string, _inspection: Partial<InspectionFeedback>): Promise<void> {
        try {
            await this.updateSubmission(submissionId, { status: 'Inspected' })
        } catch (e) {
            console.error('Failed to update submission status after inspection', e)
        }
    }

    // --- File Upload ---

    async uploadEvidence(file: File, submissionId: string): Promise<string> {
        if (this.useBackend) {
            try {
                const formData = new FormData()
                formData.append('file', file)
                formData.append('submissionId', submissionId)

                const { data: { session } } = await supabase.auth.getSession()
                const res = await fetch(`${API_URL}/api/upload`, {
                    method: 'POST',
                    headers: session?.access_token ? { 'Authorization': `Bearer ${session.access_token}` } : {},
                    body: formData
                })
                const json = await res.json()
                if (json.success && json.data?.url) {
                    return json.data.url
                }
            } catch (e) {
                console.error('Upload error:', e)
            }
        }
        // Return mock URL
        return URL.createObjectURL(file)
    }

    /** Upload a file for inspection evidence. Returns permanent URL. */
    async uploadInspectionPhoto(file: File): Promise<string> {
        if (this.useBackend) {
            try {
                const formData = new FormData()
                formData.append('file', file)
                const headers = await this.getAuthHeaders()
                const res = await fetch(`${API_URL}/api/upload`, {
                    method: 'POST',
                    headers,
                    body: formData
                })
                const json = await res.json()
                if (json.success && json.data?.url) return json.data.url
                throw new Error(json.error || 'Upload failed')
            } catch (e) {
                console.error('Inspection photo upload error:', e)
                throw e
            }
        }
        return URL.createObjectURL(file)
    }

    /**
     * Upload a shift-level evidence file (shift register, attendance sheet, etc.) to Supabase Storage
     * Returns file metadata with permanent URL
     */
    async uploadShiftLevelEvidence(params: {
        file: File
        tenderCode: string
        submissionDate: string
        shift: string
    }): Promise<{ url: string; fileName: string; name: string; type: string; size: number }> {
        if (this.useBackend) {
            try {
                // Convert file to base64
                const base64Data = await this.fileToBase64(params.file)
                
                const headers = await this.getAuthHeaders()
                const res = await fetch(`${API_URL}/api/submissions/evidence/shift-level`, {
                    method: 'POST',
                    headers: {
                        ...headers,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        base64Data,
                        tenderCode: params.tenderCode,
                        submissionDate: params.submissionDate,
                        shift: params.shift,
                        fileName: params.file.name,
                        fileType: params.file.type
                    })
                })
                
                const json = await res.json()
                if (json.success && json.data) {
                    return json.data
                }
                throw new Error(json.error || 'Upload failed')
            } catch (e) {
                console.error('Shift-level evidence upload error:', e)
                throw e
            }
        }
        // Fallback: return blob URL (temporary)
        return {
            url: URL.createObjectURL(params.file),
            fileName: params.file.name,
            name: params.file.name,
            type: params.file.type.includes('pdf') ? 'document' : 'image',
            size: params.file.size
        }
    }

    /**
     * Upload a service cycle evidence photo to Supabase Storage
     * Returns permanent URL for the uploaded file
     */
    async uploadCyclePhoto(params: {
        blobUrl: string
        tenderCode: string
        submissionDate: string
        shift: string
        serviceId: string
        cycleNumber: number
    }): Promise<string> {
        try {
            // Convert blob URL to base64
            const response = await fetch(params.blobUrl)
            const blob = await response.blob()
            const base64Data = await this.blobToBase64(blob)

            const headers = await this.getAuthHeaders()
            const res = await fetch(`${API_URL}/api/submissions/evidence/upload`, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    base64Data,
                    tenderCode: params.tenderCode,
                    submissionDate: params.submissionDate,
                    shift: params.shift,
                    serviceId: params.serviceId,
                    cycleNumber: params.cycleNumber,
                    fileType: blob.type || 'image/jpeg'
                })
            })

            const json = await res.json()
            if (json.success && json.data?.url) {
                return json.data.url
            }
            throw new Error(json.error || 'Upload failed')
        } catch (e) {
            console.error('Cycle photo upload error:', e)
            // Return original blob URL as fallback (not ideal but prevents data loss)
            return params.blobUrl
        }
    }

    /**
     * Upload all blob URLs in service entries and replace with permanent storage URLs
     */
    async uploadAllServicePhotos(params: {
        serviceEntries: Array<{
            id: string
            cyclesLogged: Array<{
                cycleNumber: number
                photos: unknown[]
            }>
        }>
        tenderCode: string
        submissionDate: string
        shift: string
    }): Promise<Array<{
        id: string
        cyclesLogged: Array<{
            cycleNumber: number
            photos: unknown[]
        }>
    }>> {
        const updatedEntries = await Promise.all(
            params.serviceEntries.map(async (entry) => {
                const updatedCycles = await Promise.all(
                    entry.cyclesLogged.map(async (cycle) => {
                        const uploadedPhotos = await Promise.all(
                            cycle.photos.map(async (photo) => {
                                const photoUrl = typeof photo === 'string'
                                  ? photo
                                  : (photo && typeof photo === 'object' && 'url' in photo
                                      ? String((photo as { url: unknown }).url ?? '')
                                      : '')
                                // Only upload if it's a blob URL
                                if (photoUrl.startsWith('blob:')) {
                                    const uploadedUrl = await this.uploadCyclePhoto({
                                        blobUrl: photoUrl,
                                        tenderCode: params.tenderCode,
                                        submissionDate: params.submissionDate,
                                        shift: params.shift,
                                        serviceId: entry.id,
                                        cycleNumber: cycle.cycleNumber
                                    })
                                    return typeof photo === 'string'
                                      ? uploadedUrl
                                      : { ...(photo as object), url: uploadedUrl }
                                }
                                // Already a permanent URL / staff audit object
                                return photo
                            })
                        )
                        return { ...cycle, photos: uploadedPhotos }
                    })
                )
                return { ...entry, cyclesLogged: updatedCycles }
            })
        )
        return updatedEntries
    }

    // Helper: Convert blob to base64
    private blobToBase64(blob: Blob): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.onloadend = () => resolve(reader.result as string)
            reader.onerror = reject
            reader.readAsDataURL(blob)
        })
    }

    private fileToBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.onloadend = () => resolve(reader.result as string)
            reader.onerror = reject
            reader.readAsDataURL(file)
        })
    }

    // --- Notifications ---
    async getNotifications(filters: { status?: string; role?: string; user_id?: string } = {}): Promise<Notification[]> {
        try {
            const params = new URLSearchParams()
            if (filters.status) params.append('status', filters.status)
            if (filters.role) params.append('role', filters.role)
            if (filters.user_id) params.append('user_id', filters.user_id)

            const headers = await this.getAuthHeaders()
            const res = await fetch(`${API_URL}/api/notifications?${params.toString()}`, { headers })
            const json = await res.json()
            if (json.success && json.data) {
                return json.data
            }
        } catch (e) {
            console.error('Get notifications error:', e)
        }
        return []
    }

    async markNotification(id: string, status: Notification['status']): Promise<Notification | undefined> {
        try {
            const headers = await this.getAuthHeaders()
            const res = await fetch(`${API_URL}/api/notifications/${id}`, {
                method: 'PATCH',
                headers,
                body: JSON.stringify({ status })
            })
            const json = await res.json()
            if (json.success && json.data) {
                return json.data
            }
        } catch (e) {
            console.error('Update notification error:', e)
        }
        return undefined
    }

    // --- Evaluations ---
    async getEvaluations(filters: { tender_id?: string; submission_id?: string; evaluator_id?: string } = {}): Promise<any[]> {
        try {
            const params = new URLSearchParams()
            if (filters.tender_id) params.append('tender_id', filters.tender_id)
            if (filters.submission_id) params.append('submission_id', filters.submission_id)
            if (filters.evaluator_id) params.append('evaluator_id', filters.evaluator_id)

            const token = await this.getAuthToken()
            const res = await fetch(`${API_URL}/api/evaluations?${params.toString()}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            })
            const json = await res.json()
            if (json.success && json.data) {
                return json.data
            }
        } catch (e) {
            console.error('Get evaluations error:', e)
        }
        return []
    }

    async getEvaluationById(id: string): Promise<any | null> {
        try {
            const token = await this.getAuthToken()
            const res = await fetch(`${API_URL}/api/evaluations/${id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            })
            const json = await res.json()
            if (json.success && json.data) {
                return json.data
            }
        } catch (e) {
            console.error('Get evaluation by ID error:', e)
        }
        return null
    }

    async getEvaluationForSubmission(submissionId: string): Promise<any | null> {
        try {
            const token = await this.getAuthToken()
            const res = await fetch(`${API_URL}/api/evaluations/submission/${submissionId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            })
            const json = await res.json()
            if (json.success && json.data) {
                return json.data
            }
        } catch (e) {
            console.error('Get evaluation for submission error:', e)
        }
        return null
    }

    async createEvaluation(evaluationData: {
        submissionId: string
        tenderId?: string
        evaluatorId: string
        evaluatorName: string
        serviceEvaluations: any[]
        evidenceApprovals?: any[]
        manpowerScore: number
        equipmentScore: number
        evidenceScore: number
        overallScore: number
        overallRemarks?: string
        recommendations?: string
        passengerFeedbackOverride?: number
        evaluatorEvidence?: any[]
    }): Promise<any | null> {
        try {
            const token = await this.getAuthToken()
            const res = await fetch(`${API_URL}/api/evaluations`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(evaluationData)
            })
            
            const json = await res.json()
            
            if (!res.ok) {
                console.error('Create evaluation failed:', {
                    status: res.status,
                    statusText: res.statusText,
                    error: json.error,
                    details: json.details
                })
                throw new Error(json.error || `Failed to create evaluation: ${res.statusText}`)
            }
            
            if (json.success && json.data) {
                return json.data
            } else {
                console.error('Create evaluation failed:', json.error)
                throw new Error(json.error || 'Failed to create evaluation')
            }
        } catch (e: any) {
            console.error('Create evaluation error:', e)
            // Re-throw with more context if it's a network error
            if (e.message && !e.message.includes('Failed to create evaluation')) {
                throw new Error(`Network error: ${e.message}`)
            }
            throw e
        }
    }

    async updateEvaluation(id: string, evaluationData: Partial<{
        serviceEvaluations: any[]
        manpowerScore: number
        equipmentScore: number
        evidenceScore: number
        overallScore: number
        overallRemarks: string
        recommendations: string
    }>): Promise<any | null> {
        try {
            const token = await this.getAuthToken()
            const res = await fetch(`${API_URL}/api/evaluations/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(evaluationData)
            })
            const json = await res.json()
            if (json.success && json.data) {
                return json.data
            }
        } catch (e) {
            console.error('Update evaluation error:', e)
        }
        return null
    }

    async deleteEvaluation(id: string): Promise<boolean> {
        try {
            const token = await this.getAuthToken()
            const res = await fetch(`${API_URL}/api/evaluations/${id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            })
            const json = await res.json()
            return json.success
        } catch (e) {
            console.error('Delete evaluation error:', e)
            return false
        }
    }

    async getEvaluationStats(tenderId?: string): Promise<any> {
        try {
            const params = new URLSearchParams()
            if (tenderId) params.append('tender_id', tenderId)

            const token = await this.getAuthToken()
            const res = await fetch(`${API_URL}/api/evaluations/stats/summary?${params.toString()}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            })
            const json = await res.json()
            if (json.success && json.data) {
                return json.data
            }
        } catch (e) {
            console.error('Get evaluation stats error:', e)
        }
        return null
    }

    // User Profile & Settings Methods
    async getCurrentUser(): Promise<any | null> {
        try {
            const { data: { user } } = await supabase.auth.getUser()
            if (!user) return null

            const token = await this.getAuthToken()
            const res = await fetch(`${API_URL}/api/users/${user.id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            })
            const json = await res.json()
            if (json.success && json.data) {
                return json.data
            }
        } catch (e) {
            console.error('Get current user error:', e)
        }
        return null
    }

    async updateCurrentUserProfile(updates: {
        full_name?: string
        phone?: string
        organization?: string
        station?: string
    }): Promise<any | null> {
        try {
            const { data: { user } } = await supabase.auth.getUser()
            if (!user) throw new Error('User not authenticated')

            const token = await this.getAuthToken()
            const res = await fetch(`${API_URL}/api/users/${user.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(updates)
            })
            const json = await res.json()
            if (json.success && json.data) {
                return json.data
            } else {
                throw new Error(json.error || 'Failed to update profile')
            }
        } catch (e: any) {
            console.error('Update profile error:', e)
            throw e
        }
    }

    async updatePassword(newPassword: string): Promise<{ error?: string }> {
        try {
            const { error } = await supabase.auth.updateUser({
                password: newPassword
            })
            if (error) {
                return { error: error.message }
            }
            return {}
        } catch (e: any) {
            console.error('Update password error:', e)
            return { error: e.message || 'Failed to update password' }
        }
    }

    async resetPassword(email: string, opts?: { asAdmin?: boolean; userId?: string }): Promise<{ error?: string; message?: string }> {
        const generic = 'If an account with that email exists, a password reset link has been sent to your email.'
        try {
            const trimmed = email.trim().toLowerCase()
            if (!trimmed) return { error: 'Enter the email for your account' }

            const session = opts?.asAdmin ? await supabase.auth.getSession() : null
            const token = session?.data.session?.access_token
            const path = opts?.asAdmin && token
                ? '/api/users/admin/password-reset'
                : '/api/users/reset-password'

            const body: { email: string; userId?: string } = { email: trimmed }
            if (opts?.asAdmin && opts.userId) {
                body.userId = opts.userId
            }

            const res = await fetch(`${API_URL}${path}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                },
                body: JSON.stringify(body)
            })

            let result: { success?: boolean; error?: string; message?: string } = {}
            try {
                result = await res.json()
            } catch {
                return { message: generic }
            }

            if (!res.ok && result.success !== true) {
                const errorMsg = result.error || `Failed to send password reset link (${res.status})`
                if (
                    !opts?.asAdmin &&
                    (errorMsg.toLowerCase().includes('not found') || errorMsg.toLowerCase().includes('does not exist'))
                ) {
                    return { message: generic }
                }
                return { error: errorMsg }
            }

            if (result.success !== false) {
                return { message: result.message || generic }
            }

            const errorMsg = result.error || 'Failed to send password reset link'
            if (
                !opts?.asAdmin &&
                (errorMsg.toLowerCase().includes('not found') || errorMsg.toLowerCase().includes('does not exist'))
            ) {
                return { message: generic }
            }
            return { error: errorMsg }
        } catch (e: unknown) {
            console.error('Reset password error:', e)
            return opts?.asAdmin
                ? { error: e instanceof Error ? e.message : 'Failed to send password reset link' }
                : { message: generic }
        }
    }

    async getUserPreferences(): Promise<any> {
        try {
            const { data: { user } } = await supabase.auth.getUser()
            if (!user) return {}

            // Get preferences from user metadata
            const { data } = await supabase.auth.getUser()
            return data.user?.user_metadata?.preferences || {
                email: true,
                sms: false,
                lockAlerts: true,
                reportReady: true,
                disputeUpdates: true
            }
        } catch (e) {
            console.error('Get preferences error:', e)
            return {
                email: true,
                sms: false,
                lockAlerts: true,
                reportReady: true,
                disputeUpdates: true
            }
        }
    }

    async saveUserPreferences(preferences: {
        email?: boolean
        sms?: boolean
        lockAlerts?: boolean
        reportReady?: boolean
        disputeUpdates?: boolean
    }): Promise<{ error?: string }> {
        try {
            const { data: { user } } = await supabase.auth.getUser()
            if (!user) throw new Error('User not authenticated')

            // Get existing metadata
            const existingMetadata = user.user_metadata || {}
            
            // Update preferences in metadata
            const { error } = await supabase.auth.updateUser({
                data: {
                    ...existingMetadata,
                    preferences: {
                        ...existingMetadata.preferences,
                        ...preferences
                    }
                }
            })

            if (error) {
                return { error: error.message }
            }
            return {}
        } catch (e: any) {
            console.error('Save preferences error:', e)
            return { error: e.message || 'Failed to save preferences' }
        }
    }

    async getUserStations(): Promise<any[]> {
        try {
            const { data: { user } } = await supabase.auth.getUser()
            if (!user) return []

            // Get user's organization and station from profile
            const userData = await this.getCurrentUser()
            if (!userData) return []

            // Get tenders for this user's organization/station
            const tenders = await this.getTenders({ status: 'active' })
            const userStations = new Set<string>()
            
            tenders.forEach((tender: Tender) => {
                if (tender.contractor === userData.organization || tender.station === userData.station) {
                    userStations.add(tender.station)
                }
            })

            return Array.from(userStations).map(station => {
                const stationTenders = tenders.filter((t: Tender) => t.station === station)
                return {
                    station,
                    division: stationTenders[0]?.division || '',
                    shifts: stationTenders.flatMap((t: Tender) => t.shifts || []).filter((v: string, i: number, arr: string[]) => arr.indexOf(v) === i)
                }
            })
        } catch (e) {
            console.error('Get user stations error:', e)
            return []
        }
    }

    // ============================================
    // INTEGRATIONS API (admin/super_admin only)
    // ============================================

    async getIntegrations(): Promise<{
        sms_provider: 'msg91' | 'fast2sms' | 'twofactor'
        msg91: { configured: boolean; sender_id?: string }
        fast2sms: { configured: boolean }
        twofactor: { configured: boolean; sender_id?: string; template_name?: string }
        email_provider: 'resend' | 'sendgrid' | 'postmark'
        sendgrid: { configured: boolean; from_email?: string }
        postmark: { configured: boolean; from_email?: string }
        resend: { configured: boolean; from_email?: string }
    }> {
        const token = await this.getAuthToken()
        const res = await fetch(`${API_URL}/api/integrations`, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        })
        const json = await res.json()
        if (!json.success) throw new Error(json.error || 'Failed to load integrations')
        return json.data
    }

    async updateMsg91Config(config: { auth_key: string; sender_id: string }): Promise<void> {
        const token = await this.getAuthToken()
        const res = await fetch(`${API_URL}/api/integrations/msg91`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            },
            body: JSON.stringify(config)
        })
        const json = await res.json()
        if (!json.success) throw new Error(json.error || 'Failed to save MSG91 config')
    }

    async testMsg91Sms(phone: string): Promise<void> {
        const token = await this.getAuthToken()
        const res = await fetch(`${API_URL}/api/integrations/msg91/test`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            },
            body: JSON.stringify({ phone })
        })
        const json = await res.json()
        if (!json.success) throw new Error(json.error || 'Failed to send test SMS')
    }

    async updateFast2SmsConfig(config: { api_key: string }): Promise<void> {
        const token = await this.getAuthToken()
        const res = await fetch(`${API_URL}/api/integrations/fast2sms`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            },
            body: JSON.stringify(config)
        })
        const json = await res.json()
        if (!json.success) throw new Error(json.error || 'Failed to save Fast2SMS config')
    }

    async testFast2SmsSms(phone: string): Promise<void> {
        const token = await this.getAuthToken()
        const res = await fetch(`${API_URL}/api/integrations/fast2sms/test`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            },
            body: JSON.stringify({ phone })
        })
        const json = await res.json()
        if (!json.success) throw new Error(json.error || 'Failed to send Fast2SMS test')
    }

    async updateTwofactorConfig(config: { api_key: string; sender_id?: string; template_name?: string }): Promise<void> {
        const token = await this.getAuthToken()
        const res = await fetch(`${API_URL}/api/integrations/twofactor`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            },
            body: JSON.stringify(config)
        })
        const json = await res.json()
        if (!json.success) throw new Error(json.error || 'Failed to save 2Factor config')
    }

    async testTwofactorSms(phone: string): Promise<void> {
        const token = await this.getAuthToken()
        const res = await fetch(`${API_URL}/api/integrations/twofactor/test`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            },
            body: JSON.stringify({ phone })
        })
        const json = await res.json()
        if (!json.success) throw new Error(json.error || 'Failed to send test OTP')
    }

    async setSmsProvider(provider: 'msg91' | 'fast2sms' | 'twofactor'): Promise<void> {
        const token = await this.getAuthToken()
        const res = await fetch(`${API_URL}/api/integrations/sms-provider`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            },
            body: JSON.stringify({ provider })
        })
        const json = await res.json()
        if (!json.success) throw new Error(json.error || 'Failed to update SMS provider')
    }

    async updateSendgridConfig(config: { api_key: string; from_email: string }): Promise<void> {
        const token = await this.getAuthToken()
        const res = await fetch(`${API_URL}/api/integrations/sendgrid`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            },
            body: JSON.stringify(config)
        })
        const json = await res.json()
        if (!json.success) throw new Error(json.error || 'Failed to save SendGrid config')
    }

    async testSendgridEmail(email: string): Promise<void> {
        const token = await this.getAuthToken()
        const res = await fetch(`${API_URL}/api/integrations/sendgrid/test`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            },
            body: JSON.stringify({ email })
        })
        const json = await res.json()
        if (!json.success) throw new Error(json.error || 'Failed to send test email')
    }

    async updatePostmarkConfig(config: { server_token: string; from_email: string }): Promise<void> {
        const token = await this.getAuthToken()
        const res = await fetch(`${API_URL}/api/integrations/postmark`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            },
            body: JSON.stringify(config)
        })
        const json = await res.json()
        if (!json.success) throw new Error(json.error || 'Failed to save Postmark config')
    }

    async testPostmarkEmail(email: string): Promise<void> {
        const token = await this.getAuthToken()
        const res = await fetch(`${API_URL}/api/integrations/postmark/test`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            },
            body: JSON.stringify({ email })
        })
        const json = await res.json()
        if (!json.success) throw new Error(json.error || 'Failed to send test email')
    }

    async updateResendConfig(config: { api_key: string; from_email: string }): Promise<void> {
        const token = await this.getAuthToken()
        const res = await fetch(`${API_URL}/api/integrations/resend`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            },
            body: JSON.stringify(config)
        })
        const json = await res.json()
        if (!json.success) throw new Error(json.error || 'Failed to save Resend config')
    }

    async testResendEmail(email: string): Promise<void> {
        const token = await this.getAuthToken()
        const res = await fetch(`${API_URL}/api/integrations/resend/test`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            },
            body: JSON.stringify({ email })
        })
        const json = await res.json()
        if (!json.success) throw new Error(json.error || 'Failed to send test email')
    }

    async setEmailProvider(provider: 'resend' | 'sendgrid' | 'postmark'): Promise<void> {
        const token = await this.getAuthToken()
        const res = await fetch(`${API_URL}/api/integrations/email-provider`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            },
            body: JSON.stringify({ provider })
        })
        const json = await res.json()
        if (!json.success) throw new Error(json.error || 'Failed to update email provider')
    }

    // ============================================
    // LOCKS & AUDIT API
    // ============================================

    async getLockSchedule(tenderId?: string): Promise<any[]> {
        try {
            const token = await this.getAuthToken()
            const params = tenderId ? `?tender_id=${tenderId}` : ''
            const res = await fetch(`${API_URL}/api/locks/schedule${params}`, {
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            })
            const json = await res.json()
            return json.success ? json.data : []
        } catch (e) {
            console.error('Get lock schedule error:', e)
            return []
        }
    }

    async getLockRecords(params?: { tenderId?: string; dateFrom?: string; dateTo?: string; limit?: number }): Promise<any[]> {
        try {
            const token = await this.getAuthToken()
            const queryParams = new URLSearchParams()
            if (params?.tenderId) queryParams.append('tender_id', params.tenderId)
            if (params?.dateFrom) queryParams.append('date_from', params.dateFrom)
            if (params?.dateTo) queryParams.append('date_to', params.dateTo)
            if (params?.limit) queryParams.append('limit', params.limit.toString())
            
            const res = await fetch(`${API_URL}/api/locks/records?${queryParams.toString()}`, {
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            })
            const json = await res.json()
            return json.success ? json.data : []
        } catch (e) {
            console.error('Get lock records error:', e)
            return []
        }
    }

    async getLockStats(tenderId?: string): Promise<any> {
        try {
            const token = await this.getAuthToken()
            const params = tenderId ? `?tender_id=${tenderId}` : ''
            const res = await fetch(`${API_URL}/api/locks/stats${params}`, {
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            })
            const json = await res.json()
            return json.success ? json.data : { totalLocked: 0, todayLocks: 0, pendingOverrides: 0, totalAuditEvents: 0 }
        } catch (e) {
            console.error('Get lock stats error:', e)
            return { totalLocked: 0, todayLocks: 0, pendingOverrides: 0, totalAuditEvents: 0 }
        }
    }

    async getOverrideRequests(params?: { tenderId?: string; status?: string; limit?: number }): Promise<any[]> {
        try {
            const token = await this.getAuthToken()
            const queryParams = new URLSearchParams()
            if (params?.tenderId) queryParams.append('tender_id', params.tenderId)
            if (params?.status) queryParams.append('status', params.status)
            if (params?.limit) queryParams.append('limit', params.limit.toString())
            
            const res = await fetch(`${API_URL}/api/locks/overrides?${queryParams.toString()}`, {
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            })
            const json = await res.json()
            return json.success ? json.data : []
        } catch (e) {
            console.error('Get override requests error:', e)
            return []
        }
    }

    async createOverrideRequest(data: { lock_record_id?: string; tender_id: string; submission_id?: string; request_date: string; shift: string; reason: string }): Promise<any> {
        try {
            const token = await this.getAuthToken()
            if (!token) {
                throw new Error('Not authenticated')
            }
            const res = await fetch(`${API_URL}/api/locks/overrides`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(data)
            })
            const json = await res.json()
            if (json.success) {
                return json.data
            } else {
                throw new Error(json.error || 'Failed to create override request')
            }
        } catch (e: any) {
            console.error('Create override request error:', e)
            throw e
        }
    }

    async updateOverrideRequest(id: string, status: 'approved' | 'rejected', reviewNotes?: string): Promise<any> {
        try {
            const token = await this.getAuthToken()
            if (!token) {
                throw new Error('Not authenticated')
            }
            const res = await fetch(`${API_URL}/api/locks/overrides/${id}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ status, review_notes: reviewNotes })
            })
            const json = await res.json()
            if (json.success) {
                return json.data
            } else {
                throw new Error(json.error || 'Failed to update override request')
            }
        } catch (e) {
            console.error('Update override request error:', e)
            throw e
        }
    }

    // Lock a submission manually
    async lockSubmission(data: { submission_id: string; tender_id: string; lock_date: string; shift: string }): Promise<any> {
        try {
            const token = await this.getAuthToken()
            if (!token) {
                throw new Error('Not authenticated')
            }
            const res = await fetch(`${API_URL}/api/locks/lock`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(data)
            })
            const json = await res.json()
            if (json.success) {
                return json.data
            } else {
                throw new Error(json.error || 'Failed to lock submission')
            }
        } catch (e) {
            console.error('Lock submission error:', e)
            throw e
        }
    }

    // Unlock a submission (admin only)
    async unlockSubmission(lockRecordId: string, reason?: string): Promise<any> {
        try {
            const token = await this.getAuthToken()
            if (!token) {
                throw new Error('Not authenticated')
            }
            const res = await fetch(`${API_URL}/api/locks/unlock`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ lock_record_id: lockRecordId, reason })
            })
            const json = await res.json()
            if (json.success) {
                return json.data
            } else {
                throw new Error(json.error || 'Failed to unlock submission')
            }
        } catch (e) {
            console.error('Unlock submission error:', e)
            throw e
        }
    }

    // Bulk lock all submissions for a date
    async bulkLockSubmissions(tenderId: string, lockDate: string): Promise<any> {
        try {
            const token = await this.getAuthToken()
            if (!token) {
                throw new Error('Not authenticated')
            }
            const res = await fetch(`${API_URL}/api/locks/lock-bulk`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ tender_id: tenderId, lock_date: lockDate })
            })
            const json = await res.json()
            if (json.success) {
                return json.data
            } else {
                throw new Error(json.error || 'Failed to bulk lock submissions')
            }
        } catch (e) {
            console.error('Bulk lock error:', e)
            throw e
        }
    }

    async getAuditTrail(params?: { tenderId?: string; limit?: number; last?: '24h' | '7d' | '30d'; eventType?: string; entityType?: string; entityId?: string }): Promise<any[]> {
        try {
            const token = await this.getAuthToken()
            const queryParams = new URLSearchParams()
            if (params?.tenderId) queryParams.append('tender_id', params.tenderId)
            if (params?.limit) queryParams.append('limit', params.limit.toString())
            if (params?.last) queryParams.append('last', params.last)
            if (params?.eventType) queryParams.append('event_type', params.eventType)
            if (params?.entityType) queryParams.append('entity_type', params.entityType)
            if (params?.entityId) queryParams.append('entity_id', params.entityId)
            
            const res = await fetch(`${API_URL}/api/audit?${queryParams.toString()}`, {
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            })
            const json = await res.json()
            return json.success ? json.data : []
        } catch (e) {
            console.error('Get audit trail error:', e)
            return []
        }
    }

    async exportAuditLog(params?: { format?: 'csv' | 'json'; dateFrom?: string; dateTo?: string }): Promise<Blob | null> {
        try {
            const token = await this.getAuthToken()
            const queryParams = new URLSearchParams()
            if (params?.format) queryParams.append('format', params.format)
            if (params?.dateFrom) queryParams.append('date_from', params.dateFrom)
            if (params?.dateTo) queryParams.append('date_to', params.dateTo)
            
            const res = await fetch(`${API_URL}/api/audit/export?${queryParams.toString()}`, {
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            })
            
            if (params?.format === 'csv') {
                return await res.blob()
            } else {
                const json = await res.json()
                return json.success ? json.data : null
            }
        } catch (e) {
            console.error('Export audit log error:', e)
            return null
        }
    }

    // ============================================
    // REPORTS API
    // ============================================

    async getReportQueue(params?: { status?: string; limit?: number }): Promise<any[]> {
        try {
            const token = await this.getAuthToken()
            const queryParams = new URLSearchParams()
            if (params?.status) queryParams.append('status', params.status)
            if (params?.limit) queryParams.append('limit', params.limit.toString())
            
            const res = await fetch(`${API_URL}/api/reports/queue?${queryParams.toString()}`, {
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            })
            const json = await res.json()
            return json.success ? json.data : []
        } catch (e) {
            console.error('Get report queue error:', e)
            return []
        }
    }

    async getReportStats(): Promise<any> {
        try {
            const token = await this.getAuthToken()
            const res = await fetch(`${API_URL}/api/reports/stats`, {
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            })
            const json = await res.json()
            return json.success ? json.data : { reportsReady: 0, generating: 0, scheduled: 0 }
        } catch (e) {
            console.error('Get report stats error:', e)
            return { reportsReady: 0, generating: 0, scheduled: 0 }
        }
    }

    async getScheduledReports(): Promise<any[]> {
        try {
            const token = await this.getAuthToken()
            const res = await fetch(`${API_URL}/api/reports/scheduled`, {
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            })
            const json = await res.json()
            return json.success ? json.data : []
        } catch (e) {
            console.error('Get scheduled reports error:', e)
            return []
        }
    }

    async getReportTemplates(): Promise<any[]> {
        try {
            const token = await this.getAuthToken()
            const res = await fetch(`${API_URL}/api/reports/templates`, {
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            })
            const json = await res.json()
            return json.success ? json.data : []
        } catch (e) {
            console.error('Get report templates error:', e)
            return []
        }
    }

    async generateReport(data: { name: string; report_type: string; format: 'PDF' | 'Excel' | 'CSV'; tender_id?: string; filters?: any }): Promise<any> {
        try {
            const token = await this.getAuthToken()
            const res = await fetch(`${API_URL}/api/reports/generate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(data)
            })
            const json = await res.json()
            if (json.success) {
                return json.data
            } else {
                throw new Error(json.error || 'Failed to generate report')
            }
        } catch (e: any) {
            console.error('Generate report error:', e)
            throw e
        }
    }

    async downloadReport(id: string): Promise<void> {
        try {
            const token = await this.getAuthToken()
            const res = await fetch(`${API_URL}/api/reports/${id}/download`, {
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            })
            
            if (res.ok) {
                const blob = await res.blob()
                const url = window.URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = url
                a.download = `report-${id}.${blob.type.includes('pdf') ? 'pdf' : blob.type.includes('excel') ? 'xlsx' : 'csv'}`
                document.body.appendChild(a)
                a.click()
                window.URL.revokeObjectURL(url)
                document.body.removeChild(a)
            } else {
                throw new Error('Failed to download report')
            }
        } catch (e: any) {
            console.error('Download report error:', e)
            throw e
        }
    }

    // ============================================
    // STATIONS API
    // ============================================

    async getStations(activeOnly: boolean = true): Promise<any[]> {
        try {
            const token = await this.getAuthToken()
            const params = activeOnly ? '?active_only=true' : '?active_only=false'
            const res = await fetch(`${API_URL}/api/stations${params}`, {
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            })
            const json = await res.json()
            return json.success ? json.data : []
        } catch (e) {
            console.error('Get stations error:', e)
            return []
        }
    }

    async getStationByCode(code: string): Promise<any | null> {
        return this.getStation(code)
    }

    async getStation(idOrCode: string): Promise<any | null> {
        try {
            const token = await this.getAuthToken()
            const res = await fetch(`${API_URL}/api/stations/${encodeURIComponent(idOrCode)}`, {
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            })
            const json = await res.json()
            return json.success ? json.data : null
        } catch (e) {
            console.error('Get station error:', e)
            return null
        }
    }

    async createStation(data: { code: string; name: string; division?: string; state?: string; district?: string; zone?: string; latitude?: number; longitude?: number; is_active?: boolean }): Promise<any> {
        try {
            const res = await this.fetchWithAuth(`${API_URL}/api/stations`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            })
            const json = await res.json()
            if (json.success) {
                return json.data
            }
            if (res.status === 401) {
                throw new Error('Session expired — please sign in again and retry')
            }
            throw new Error(json.error || 'Failed to create station')
        } catch (e: any) {
            console.error('Create station error:', e)
            throw e
        }
    }

    async updateStation(id: string, data: Partial<{ code: string; name: string; division?: string; state?: string; district?: string; zone?: string; latitude?: number; longitude?: number; is_active?: boolean }>): Promise<any> {
        try {
            const res = await this.fetchWithAuth(`${API_URL}/api/stations/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            })
            const json = await res.json()
            if (json.success) {
                return json.data
            }
            if (res.status === 401) {
                throw new Error('Session expired — please sign in again and retry')
            }
            throw new Error(json.error || 'Failed to update station')
        } catch (e: any) {
            console.error('Update station error:', e)
            throw e
        }
    }

    async deleteStation(id: string): Promise<void> {
        try {
            const res = await this.fetchWithAuth(`${API_URL}/api/stations/${id}`, {
                method: 'DELETE'
            })
            const json = await res.json()
            if (!json.success) {
                if (res.status === 401) {
                    throw new Error('Session expired — please sign in again and retry')
                }
                throw new Error(json.error || 'Failed to delete station')
            }
        } catch (e: any) {
            console.error('Delete station error:', e)
            throw e
        }
    }

    async cleanupTestStations(): Promise<{ deleted: Array<{ code: string; name: string }>; skipped: Array<{ code: string; reason: string }> }> {
        const res = await this.fetchWithAuth(`${API_URL}/api/stations/cleanup-test`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
        })
        const json = await res.json()
        if (!json.success) {
            if (res.status === 401) {
                throw new Error('Session expired — please sign in again and retry')
            }
            throw new Error(json.error || 'Failed to clean test stations')
        }
        return json.data
    }

    // ============================================
    // USERS API (admin)
    // ============================================

    async getUsers(): Promise<{ id: string; email: string; full_name: string; roles: string[]; station: string | null; assigned_stations?: string[] | null; is_active: boolean; phone?: string | null; gst_number?: string | null; organization?: string | null }[]> {
        try {
            const token = await this.getAuthToken()
            const res = await fetch(`${API_URL}/api/users`, {
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            })
            const json = await res.json()
            if (json.success && json.data) {
                return json.data
            }
        } catch (e) {
            console.error('Get users error:', e)
        }
        return []
    }

    async getUserStats(): Promise<{ totalUsers: number; activeUsers: number; byRole: Record<string, number> } | null> {
        try {
            const token = await this.getAuthToken()
            const res = await fetch(`${API_URL}/api/users/stats`, {
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            })
            const json = await res.json()
            if (json.success && json.data) return json.data
        } catch (e) {
            console.error('Get user stats error:', e)
        }
        return null
    }

    // ============================================
    // CONTRACTORS API
    // ============================================

    async getContractors(activeOnly: boolean = true): Promise<{ id: string; name: string; organization: string; email: string; station?: string }[]> {
        try {
            const token = await this.getAuthToken()
            const res = await fetch(`${API_URL}/api/users/by-role/contractor`, {
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            })
            const json = await res.json()
            if (json.success && json.data) {
                return json.data
                    .filter((user: any) => !activeOnly || user.is_active !== false)
                    .map((user: any) => ({
                        id: user.id,
                        name: user.full_name || user.organization || user.email.split('@')[0],
                        organization: user.organization || user.full_name || user.email.split('@')[0],
                        email: user.email,
                        station: user.station
                    }))
            }
        } catch (e) {
            console.error('Get contractors error:', e)
        }
        return []
    }

    async searchUsers(q: string, role?: 'contractor' | 'contractor_staff' | 'inspector' | 'buyer'): Promise<{ id: string; full_name: string; email: string; organization?: string; services?: string[] }[]> {
        try {
            const token = await this.getAuthToken()
            if (!token) {
                console.error('[API] Search users: no auth token')
                throw new Error('Please sign in to search users')
            }
            const params = new URLSearchParams()
            if (q) params.set('q', q.trim())
            if (role) params.set('role', role)
            const query = params.toString()
            const res = await fetch(`${API_URL}/api/users/search${query ? `?${query}` : ''}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            })
            const json = await res.json()
            if (!res.ok) {
                const msg = json?.error || `Search failed (${res.status})`
                throw new Error(msg)
            }
            if (json.success && json.data) {
                return json.data.map((u: any) => ({
                    id: u.id,
                    full_name: u.full_name || u.email?.split('@')[0] || 'Unknown',
                    email: u.email || '',
                    organization: u.organization,
                    services: Array.isArray(u.services) ? u.services : undefined
                }))
            }
            return []
        } catch (e) {
            console.error('Search users error:', e)
            throw e
        }
    }

    // ============================================
    // QR API
    // ============================================

    async getTenderServiceAssets(tenderId: string, serviceId?: string): Promise<Array<{ id: string; tender_id: string; service_id: string; asset_id: string; geofence_required: boolean; station_asset?: { id: string; name: string; asset_type: string; latitude: number; longitude: number; geofence_radius_meters: number } }>> {
        try {
            const headers = await this.getAuthHeaders()
            const params = new URLSearchParams()
            if (serviceId) params.append('service_id', serviceId)
            const query = params.toString()
            const res = await fetch(`${API_URL}/api/tenders/${tenderId}/service-assets${query ? `?${query}` : ''}`, {
                headers
            })
            const json = await res.json()
            if (json.success && json.data) return json.data
            return []
        } catch (e) {
            console.error('Get tender service assets error:', e)
            return []
        }
    }

    async createTenderServiceAsset(tenderId: string, payload: { service_id: string; asset_id: string; geofence_required?: boolean }): Promise<{ id: string }> {
        const headers = await this.getAuthHeaders()
        const res = await fetch(`${API_URL}/api/tenders/${tenderId}/service-assets`, {
            method: 'POST',
            headers: { ...headers, 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...payload, geofence_required: payload.geofence_required ?? true })
        })
        const json = await res.json()
        if (!json.success) throw new Error(json.error || 'Failed to link asset')
        return json.data
    }

    async deleteTenderServiceAsset(tenderId: string, mappingId: string): Promise<void> {
        const headers = await this.getAuthHeaders()
        const res = await fetch(`${API_URL}/api/tenders/${tenderId}/service-assets/${mappingId}`, {
            method: 'DELETE',
            headers
        })
        const json = await res.json()
        if (!json.success) throw new Error(json.error || 'Failed to unlink asset')
    }

    async getStationAssets(stationId: string): Promise<Array<{ id: string; name: string; asset_type: string; latitude: number; longitude: number; geofence_radius_meters?: number }>> {
        const headers = await this.getAuthHeaders()
        const res = await fetch(`${API_URL}/api/stations/${stationId}/assets`, { headers })
        const json = await res.json()
        if (json.success && json.data) return json.data
        return []
    }

    async generateQR(payload: { station_code: string; service_key: string; tender_id?: string; asset_id?: string; label?: string }): Promise<{ qr_data_url: string; url: string; station_code: string; service_key: string; tender_id?: string; asset_id?: string; label?: string }> {
        try {
            const headers = await this.getAuthHeaders()
            const res = await fetch(`${API_URL}/api/qr/generate`, {
                method: 'POST',
                headers,
                body: JSON.stringify(payload)
            })
            const json = await res.json()
            if (json.success && json.data) {
                return json.data
            }
            throw new Error(json.error || 'Failed to generate QR')
        } catch (e: any) {
            console.error('Generate QR error:', e)
            throw e
        }
    }

    async generateQRBatch(payload: { station_code: string; service_keys: string[]; tender_id?: string; asset_ids?: (string | null)[]; labels?: (string | undefined)[] }): Promise<Array<{ service_key: string; asset_id?: string | null; qr_data_url: string; url: string; label?: string }>> {
        try {
            const headers = await this.getAuthHeaders()
            const res = await fetch(`${API_URL}/api/qr/batch`, {
                method: 'POST',
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            })
            const json = await res.json()
            if (json.success && json.data) {
                return json.data
            }
            throw new Error(json.error || 'Failed to generate QR codes')
        } catch (e: any) {
            console.error('Generate QR batch error:', e)
            throw e
        }
    }

    async saveQRRecords(tenderId: string, records: Array<{ service_id: string; asset_id?: string | null; generated_url: string }>): Promise<void> {
        try {
            const headers = await this.getAuthHeaders()
            const res = await fetch(`${API_URL}/api/qr/records`, {
                method: 'POST',
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    records: records.map(r => ({ tender_id: tenderId, ...r }))
                })
            })
            const json = await res.json()
            if (!json.success) throw new Error(json.error || 'Failed to save QR records')
        } catch (e) {
            console.error('Save QR records error:', e)
            throw e
        }
    }

    async getQRRecords(tenderId: string, limit?: number): Promise<Array<{ id: string; tender_id: string; service_id: string; asset_id: string | null; generated_url: string; generated_at: string }>> {
        try {
            const headers = await this.getAuthHeaders()
            const params = new URLSearchParams({ tender_id: tenderId })
            if (limit) params.append('limit', String(limit))
            const res = await fetch(`${API_URL}/api/qr/records?${params.toString()}`, { headers })
            const json = await res.json()
            if (json.success && json.data) return json.data
            return []
        } catch (e) {
            console.error('Get QR records error:', e)
            return []
        }
    }

    // ============================================
    // FEEDBACK API
    // ============================================

    async getPublicFeedbackForm(station_code?: string, tender_id?: string): Promise<any | null> {
        try {
            const params = new URLSearchParams()
            if (station_code) params.append('station_code', station_code)
            if (tender_id) params.append('tender_id', tender_id)
            const res = await fetch(`${API_URL}/api/feedback/forms?${params.toString()}`)
            const json = await res.json()
            if (json.success && json.data) {
                return json.data
            }
        } catch (e) {
            console.error('Get public feedback form error:', e)
        }
        return null
    }

    async submitPassengerFeedback(payload: { station_code: string; service_key: string; pnr: string; mobile: string; tender_id?: string; form_id?: string; overall_rating?: number; responses?: Record<string, unknown> }): Promise<any> {
        try {
            const res = await fetch(`${API_URL}/api/feedback`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            })
            const json = await res.json()
            if (json.success) {
                return json.data
            }
            throw new Error(json.error || 'Failed to submit feedback')
        } catch (e: any) {
            console.error('Submit passenger feedback error:', e)
            throw e
        }
    }

    async getFeedbackList(params: { station_code?: string; tender_id?: string; service_key?: string; from?: string; to?: string }): Promise<{ data: any[]; meta: { total: number; average_rating: number } }> {
        try {
            const queryParams = new URLSearchParams()
            if (params.station_code) queryParams.append('station_code', params.station_code)
            if (params.tender_id) queryParams.append('tender_id', params.tender_id)
            if (params.service_key) queryParams.append('service_key', params.service_key)
            if (params.from) queryParams.append('from', params.from)
            if (params.to) queryParams.append('to', params.to)
            const token = await this.getAuthToken()
            const res = await fetch(`${API_URL}/api/feedback?${queryParams.toString()}`, {
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            })
            const json = await res.json()
            if (json.success) {
                return { data: json.data || [], meta: json.meta || { total: 0, average_rating: 0 } }
            }
        } catch (e) {
            console.error('Get feedback list error:', e)
        }
        return { data: [], meta: { total: 0, average_rating: 0 } }
    }

    async createFeedbackForm(payload: { station_code?: string; tender_id?: string; title: string; questions: unknown[]; is_active?: boolean }): Promise<any> {
        try {
            const headers = await this.getAuthHeaders()
            const res = await fetch(`${API_URL}/api/feedback/forms`, {
                method: 'POST',
                headers,
                body: JSON.stringify(payload)
            })
            const json = await res.json()
            if (json.success && json.data) {
                return json.data
            }
        } catch (e) {
            console.error('Create feedback form error:', e)
        }
        return null
    }

    async updateFeedbackForm(id: string, payload: Partial<{ title: string; questions: unknown[]; is_active: boolean }>): Promise<any> {
        try {
            const headers = await this.getAuthHeaders()
            const res = await fetch(`${API_URL}/api/feedback/forms/${id}`, {
                method: 'PATCH',
                headers,
                body: JSON.stringify(payload)
            })
            const json = await res.json()
            if (json.success && json.data) {
                return json.data
            }
        } catch (e) {
            console.error('Update feedback form error:', e)
        }
        return null
    }

    async deleteFeedbackForm(id: string): Promise<boolean> {
        try {
            const token = await this.getAuthToken()
            const res = await fetch(`${API_URL}/api/feedback/forms/${id}`, {
                method: 'DELETE',
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            })
            const json = await res.json()
            return json.success
        } catch (e) {
            console.error('Delete feedback form error:', e)
            return false
        }
    }

    // ============================================
    // INSPECTIONS API
    // ============================================

    async getInspections(params?: { tender_id?: string; station_code?: string; submission_id?: string; type?: string; from?: string; to?: string; limit?: number }): Promise<any[]> {
        try {
            const queryParams = new URLSearchParams()
            if (params?.tender_id) queryParams.append('tender_id', params.tender_id)
            if (params?.station_code) queryParams.append('station_code', params.station_code)
            if (params?.submission_id) queryParams.append('submission_id', params.submission_id)
            if (params?.type) queryParams.append('type', params.type)
            if (params?.from) queryParams.append('from', params.from)
            if (params?.to) queryParams.append('to', params.to)
            if (params?.limit) queryParams.append('limit', params.limit.toString())
            const token = await this.getAuthToken()
            const res = await fetch(`${API_URL}/api/inspections?${queryParams.toString()}`, {
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            })
            const json = await res.json()
            return json.success ? json.data : []
        } catch (e) {
            console.error('Get inspections error:', e)
            return []
        }
    }

    async getInspection(id: string): Promise<any | null> {
        try {
            const token = await this.getAuthToken()
            const res = await fetch(`${API_URL}/api/inspections/${id}`, {
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            })
            const json = await res.json()
            return json.success ? json.data : null
        } catch (e) {
            console.error('Get inspection error:', e)
            return null
        }
    }

    async createInspection(payload: { tender_id: string; station_code: string; submission_id?: string; inspector_name: string; inspection_type: 'scheduled' | 'adhoc'; service_keys: string[]; checklist_results: Array<{ id: string; label: string; passed: boolean; weight?: number; notes?: string; photo?: string }>; remarks?: string; photos?: string[]; inspected_at?: string }): Promise<any> {
        try {
            const headers = await this.getAuthHeaders()
            const res = await fetch(`${API_URL}/api/inspections`, {
                method: 'POST',
                headers,
                body: JSON.stringify(payload)
            })
            const json = await res.json()
            if (json.success && json.data) {
                return json.data
            }
        } catch (e) {
            console.error('Create inspection error:', e)
        }
        return null
    }

    async updateInspection(id: string, payload: Partial<{ checklist_results: unknown[]; remarks: string; photos: string[]; overall_score: number }>): Promise<any | null> {
        try {
            const headers = await this.getAuthHeaders()
            const res = await fetch(`${API_URL}/api/inspections/${id}`, {
                method: 'PATCH',
                headers,
                body: JSON.stringify(payload)
            })
            const json = await res.json()
            return json.success ? json.data : null
        } catch (e) {
            console.error('Update inspection error:', e)
            return null
        }
    }

    async deleteInspection(id: string): Promise<boolean> {
        try {
            const token = await this.getAuthToken()
            const res = await fetch(`${API_URL}/api/inspections/${id}`, {
                method: 'DELETE',
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            })
            const json = await res.json()
            return json.success
        } catch (e) {
            console.error('Delete inspection error:', e)
            return false
        }
    }

    // ============================================
    // TENDER STAFF API
    // ============================================

    async getTenderStaff(tenderId: string): Promise<any[]> {
        try {
            const token = await this.getAuthToken()
            const res = await fetch(`${API_URL}/api/tenders/${tenderId}/staff`, {
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            })
            const json = await res.json()
            return json.success ? json.data : []
        } catch (e) {
            console.error('Get tender staff error:', e)
            return []
        }
    }

    async addTenderStaff(tenderId: string, payload: { user_id: string; station_code: string; can_submit?: boolean }): Promise<any> {
        const headers = await this.getAuthHeaders()
        const res = await fetch(`${API_URL}/api/tenders/${tenderId}/staff`, {
            method: 'POST',
            headers,
            body: JSON.stringify(payload)
        })
        const json = await res.json().catch(() => ({}))
        if (!res.ok || !json.success || !json.data) {
            throw new Error(json.error || 'Failed to add staff member')
        }
        return json.data
    }

    async updateTenderStaff(
        tenderId: string,
        userId: string,
        payload: { station_code?: string; can_submit?: boolean }
    ): Promise<any> {
        try {
            const headers = await this.getAuthHeaders()
            const res = await fetch(`${API_URL}/api/tenders/${tenderId}/staff/${userId}`, {
                method: 'PATCH',
                headers,
                body: JSON.stringify(payload)
            })
            const json = await res.json()
            if (!json.success) {
                throw new Error(json.error || 'Failed to update staff')
            }
            return json.data
        } catch (e) {
            console.error('Update tender staff error:', e)
            throw e
        }
    }

    async removeTenderStaff(tenderId: string, userId: string): Promise<boolean> {
        try {
            const token = await this.getAuthToken()
            const res = await fetch(`${API_URL}/api/tenders/${tenderId}/staff/${userId}`, {
                method: 'DELETE',
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            })
            const json = await res.json()
            return json.success
        } catch (e) {
            console.error('Remove tender staff error:', e)
            return false
        }
    }

    async getMyTenders(): Promise<any[]> {
        try {
            const token = await this.getAuthToken()
            const res = await fetch(`${API_URL}/api/tender-staff/my-tenders`, {
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            })
            const json = await res.json()
            return json.success ? json.data : []
        } catch (e) {
            console.error('Get my tenders error:', e)
            return []
        }
    }

    async addStationEvaluator(payload: { evaluator_id: string; station_code: string; tender_id?: string }): Promise<any> {
        const headers = await this.getAuthHeaders()
        const res = await fetch(`${API_URL}/api/tender-staff/station-evaluators`, {
            method: 'POST',
            headers,
            body: JSON.stringify(payload)
        })
        const json = await res.json()
        if (!res.ok) {
            const msg = json?.details?.length
                ? json.details.map((d: { field?: string; message?: string }) => d.message).join(', ')
                : (json?.error || `Failed to add evaluator (${res.status})`)
            throw new Error(msg)
        }
        return json.success && json.data ? json.data : null
    }

    async removeStationEvaluator(stationCode: string, evaluatorId: string, tenderId?: string): Promise<boolean> {
        try {
            const headers = await this.getAuthHeaders()
            const res = await fetch(`${API_URL}/api/tender-staff/station-evaluators`, {
                method: 'DELETE',
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify({ station_code: stationCode, evaluator_id: evaluatorId, tender_id: tenderId })
            })
            const json = await res.json()
            return json.success === true
        } catch (e) {
            console.error('Remove station evaluator error:', e)
            return false
        }
    }

    async getStationEvaluators(stationCode: string, tenderId?: string): Promise<Array<{ evaluator_id: string; evaluator?: { full_name?: string; email?: string } }>> {
        try {
            const token = await this.getAuthToken()
            const q = tenderId ? `?tender_id=${encodeURIComponent(tenderId)}` : ''
            const res = await fetch(`${API_URL}/api/tender-staff/station-evaluators/${stationCode}${q}`, {
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            })
            const json = await res.json()
            return json.success && Array.isArray(json.data) ? json.data : []
        } catch (e) {
            console.error('Get station evaluators error:', e)
            return []
        }
    }

    // ============================================
    // CONTRACTOR STAFF API
    // ============================================

    async getContractorStaffByTender(tenderId: string): Promise<any[]> {
        const token = await this.getAuthToken()
        if (!token) {
            throw new Error('Please sign in to view staff')
        }
        const res = await fetch(`${API_URL}/api/contractor-staff/by-tender/${encodeURIComponent(tenderId)}`, {
            headers: { 'Authorization': `Bearer ${token}` }
        })
        const json = await res.json()
        if (!res.ok) {
            throw new Error(json?.error || `Failed to load staff (${res.status})`)
        }
        return json.success && Array.isArray(json.data) ? json.data : []
    }

    async getContractorStaffByOrg(org: string): Promise<any[]> {
        const token = await this.getAuthToken()
        if (!token) {
            throw new Error('Please sign in to view staff')
        }
        const res = await fetch(`${API_URL}/api/contractor-staff/by-org/${encodeURIComponent(org)}`, {
            headers: { 'Authorization': `Bearer ${token}` }
        })
        const json = await res.json()
        if (!res.ok) {
            throw new Error(json?.error || `Failed to load staff (${res.status})`)
        }
        return json.success && Array.isArray(json.data) ? json.data : []
    }

    async createContractorStaff(payload: Record<string, unknown>): Promise<void> {
        const token = await this.getAuthToken()
        const res = await fetch(`${API_URL}/api/contractor-staff`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            },
            body: JSON.stringify(payload)
        })
        const json = await res.json()
        if (!json.success) {
            throw new Error(json.error || 'Failed to create staff')
        }
    }

    async removeContractorStaffFromTender(userId: string, tenderId: string): Promise<void> {
        const token = await this.getAuthToken()
        const res = await fetch(`${API_URL}/api/contractor-staff/${userId}/tender/${tenderId}`, {
            method: 'DELETE',
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        })
        const json = await res.json()
        if (!json.success) {
            throw new Error(json.error || 'Failed to remove staff')
        }
    }

    async assignContractorStaffServices(tenderStaffId: string, serviceIds: string[]): Promise<void> {
        const token = await this.getAuthToken()
        const res = await fetch(`${API_URL}/api/contractor-staff/${tenderStaffId}/services`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            },
            body: JSON.stringify({ service_ids: serviceIds })
        })
        const json = await res.json()
        if (!json.success) {
            throw new Error(json.error || 'Failed to assign services')
        }
    }

    async removeContractorStaffService(tenderStaffId: string, serviceId: string): Promise<void> {
        const token = await this.getAuthToken()
        const res = await fetch(`${API_URL}/api/contractor-staff/${tenderStaffId}/services/${serviceId}`, {
            method: 'DELETE',
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        })
        const json = await res.json()
        if (!json.success) {
            throw new Error(json.error || 'Failed to remove service')
        }
    }

    async getStaffAssets(tenderStaffId: string): Promise<Array<{ id: string; asset_id: string; station_asset?: { id: string; name: string; asset_type: string } }>> {
        const token = await this.getAuthToken()
        const res = await fetch(`${API_URL}/api/contractor-staff/${tenderStaffId}/assets`, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        })
        const json = await res.json()
        if (!res.ok) throw new Error(json?.error || 'Failed to fetch staff assets')
        return json.success && Array.isArray(json.data) ? json.data : []
    }

    async assignStaffAssets(tenderStaffId: string, assetIds: string[]): Promise<void> {
        const token = await this.getAuthToken()
        const res = await fetch(`${API_URL}/api/contractor-staff/${tenderStaffId}/assets`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            },
            body: JSON.stringify({ asset_ids: assetIds })
        })
        const json = await res.json()
        if (!json.success) throw new Error(json.error || 'Failed to assign assets')
    }

    async removeStaffAsset(tenderStaffId: string, assetId: string): Promise<void> {
        const token = await this.getAuthToken()
        const res = await fetch(`${API_URL}/api/contractor-staff/${tenderStaffId}/assets/${assetId}`, {
            method: 'DELETE',
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        })
        const json = await res.json()
        if (!json.success) throw new Error(json.error || 'Failed to remove asset')
    }

    // ============================================
    // CHI ANALYTICS API
    // ============================================

    async getDailyAnalytics(station_code: string, tender_id?: string, date?: string): Promise<any | null> {
        try {
            const params = new URLSearchParams({ station_code })
            if (tender_id) params.append('tender_id', tender_id)
            if (date) params.append('date', date)
            const token = await this.getAuthToken()
            const res = await fetch(`${API_URL}/api/chi/analytics/daily?${params.toString()}`, {
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            })
            const json = await res.json()
            return json.success ? json.data : null
        } catch (e) {
            console.error('Get daily analytics error:', e)
            return null
        }
    }

    // ============================================
    // Generic REST helpers for new feature pages
    // ============================================

    /** Generic GET - path should start with / e.g. '/supplies/items' */
    async get(path: string): Promise<any> {
        const headers = await this.getAuthHeaders()
        const url = `${API_URL}/api${this.normalizeGenericApiPath(path)}`
        const res = await fetch(url, { headers })
        const json = await res.json()
        if (!res.ok) throw new Error(json.error || `Request failed: ${res.status}`)
        return json.success !== undefined ? json.data : json
    }

    /** Generic POST - path should start with / e.g. '/supplies/items' */
    async post(path: string, body: unknown): Promise<any> {
        const headers = await this.getAuthHeaders()
        const url = `${API_URL}/api${this.normalizeGenericApiPath(path)}`
        const res = await fetch(url, {
            method: 'POST',
            headers,
            body: JSON.stringify(body)
        })
        const json = await res.json()
        if (!res.ok) throw new Error(json.error || `Request failed: ${res.status}`)
        return json.success !== undefined ? json.data : json
    }

    /** Generic PUT - path should start with / e.g. '/supplies/items/:id' */
    async put(path: string, body: unknown): Promise<any> {
        const headers = await this.getAuthHeaders()
        const url = `${API_URL}/api${this.normalizeGenericApiPath(path)}`
        const res = await fetch(url, {
            method: 'PUT',
            headers,
            body: JSON.stringify(body)
        })
        const json = await res.json()
        if (!res.ok) throw new Error(json.error || `Request failed: ${res.status}`)
        return json.success !== undefined ? json.data : json
    }

    /** Generic PATCH - path should start with / e.g. '/dashboard/chi/supervisors/:id' */
    async patch (path: string, body: unknown): Promise<any> {
        const headers = await this.getAuthHeaders()
        const url = `${API_URL}/api${this.normalizeGenericApiPath(path)}`
        const res = await fetch(url, {
            method: 'PATCH',
            headers,
            body: JSON.stringify(body)
        })
        const json = await res.json()
        if (!res.ok) throw new Error(json.error || `Request failed: ${res.status}`)
        return json.success !== undefined ? json.data : json
    }

    /** Generic DELETE */
    async delete(path: string): Promise<any> {
        const headers = await this.getAuthHeaders()
        const url = `${API_URL}/api${this.normalizeGenericApiPath(path)}`
        const res = await fetch(url, { method: 'DELETE', headers })
        const json = await res.json()
        if (!res.ok) throw new Error(json.error || `Request failed: ${res.status}`)
        return json.success !== undefined ? json.data : json
    }

    async getMonthlyAnalytics(station_code: string, tender_id?: string, month?: string): Promise<any | null> {
        try {
            const params = new URLSearchParams({ station_code })
            if (tender_id) params.append('tender_id', tender_id)
            if (month) params.append('month', month)
            const token = await this.getAuthToken()
            const res = await fetch(`${API_URL}/api/chi/analytics/monthly?${params.toString()}`, {
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            })
            const json = await res.json()
            return json.success ? json.data : null
        } catch (e) {
            console.error('Get monthly analytics error:', e)
            return null
        }
    }

    // ============================================
    // REPORTS ANALYTICS API
    // ============================================

    async getStationComparison(params: {
        tender_id: string
        date_from?: string
        date_to?: string
        limit?: number
    }): Promise<any[]> {
        try {
            const queryParams = new URLSearchParams()
            queryParams.append('tender_id', params.tender_id)
            if (params.date_from) queryParams.append('date_from', params.date_from)
            if (params.date_to) queryParams.append('date_to', params.date_to)
            if (params.limit) queryParams.append('limit', params.limit.toString())

            const res = await fetch(`${API_URL}/api/reports/station-comparison?${queryParams.toString()}`, {
                headers: await this.getAuthHeaders()
            })
            const json = await res.json()
            return json.success ? json.data : []
        } catch (e: any) {
            console.error('Get station comparison error:', e)
            throw e
        }
    }

    async getTrends(params: {
        tender_id?: string
        station_code?: string
        date_from?: string
        date_to?: string
        granularity?: 'daily' | 'weekly' | 'monthly'
        limit?: number
    }): Promise<any[]> {
        try {
            const queryParams = new URLSearchParams()
            if (params.tender_id) queryParams.append('tender_id', params.tender_id)
            if (params.station_code) queryParams.append('station_code', params.station_code)
            if (params.date_from) queryParams.append('date_from', params.date_from)
            if (params.date_to) queryParams.append('date_to', params.date_to)
            if (params.granularity) queryParams.append('granularity', params.granularity)
            if (params.limit) queryParams.append('limit', params.limit.toString())

            const res = await fetch(`${API_URL}/api/reports/trends?${queryParams.toString()}`, {
                headers: await this.getAuthHeaders()
            })
            const json = await res.json()
            return json.success ? json.data : []
        } catch (e: any) {
            console.error('Get trends error:', e)
            throw e
        }
    }

    async getComplianceScorecard(params: {
        tender_id: string
        date_from?: string
        date_to?: string
    }): Promise<any> {
        try {
            const queryParams = new URLSearchParams()
            queryParams.append('tender_id', params.tender_id)
            if (params.date_from) queryParams.append('date_from', params.date_from)
            if (params.date_to) queryParams.append('date_to', params.date_to)

            const res = await fetch(`${API_URL}/api/reports/compliance-scorecard?${queryParams.toString()}`, {
                headers: await this.getAuthHeaders()
            })
            const json = await res.json()
            return json.success ? json.data : null
        } catch (e: any) {
            console.error('Get compliance scorecard error:', e)
            throw e
        }
    }

    async getReportBundle (params: {
        tender_id?: string
        date_from?: string
        date_to?: string
        granularity?: 'daily' | 'weekly' | 'monthly'
        include?: string
    }): Promise<unknown> {
        try {
            const queryParams = new URLSearchParams()
            if (params.tender_id) queryParams.append('tender_id', params.tender_id)
            if (params.date_from) queryParams.append('date_from', params.date_from)
            if (params.date_to) queryParams.append('date_to', params.date_to)
            if (params.granularity) queryParams.append('granularity', params.granularity)
            if (params.include) queryParams.append('include', params.include)
            const res = await fetch(`${API_URL}/api/reports/bundle?${queryParams.toString()}`, {
                headers: await this.getAuthHeaders()
            })
            const json = await res.json() as { success?: boolean; data?: unknown; error?: string }
            if (!res.ok) throw new Error(json.error || 'Failed to load report bundle')
            return json.success ? json.data : null
        } catch (e) {
            console.error('getReportBundle error:', e)
            throw e
        }
    }

    async downloadReportPdf (params: {
        tender_id?: string
        date_from?: string
        date_to?: string
        granularity?: 'daily' | 'weekly' | 'monthly'
        include?: string
        mode: 'combined' | 'single'
        section?: 'station-comparison' | 'trends' | 'compliance'
    }): Promise<void> {
        try {
            const queryParams = new URLSearchParams()
            queryParams.append('mode', params.mode)
            if (params.tender_id) queryParams.append('tender_id', params.tender_id)
            if (params.date_from) queryParams.append('date_from', params.date_from)
            if (params.date_to) queryParams.append('date_to', params.date_to)
            if (params.granularity) queryParams.append('granularity', params.granularity)
            if (params.include) queryParams.append('include', params.include)
            if (params.mode === 'single' && params.section) {
                queryParams.append('section', params.section)
            }
            const res = await fetch(`${API_URL}/api/reports/export/pdf?${queryParams.toString()}`, {
                headers: await this.getAuthHeaders()
            })
            if (!res.ok) {
                const err = await res.json().catch(() => ({})) as { error?: string }
                throw new Error(err.error || 'Failed to download PDF')
            }
            const blob = await res.blob()
            const cd = res.headers.get('Content-Disposition')
            let name = 'report.pdf'
            if (cd) {
                const m = /filename="([^"]+)"/.exec(cd)
                if (m) name = m[1]
            }
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = name
            document.body.appendChild(a)
            a.click()
            window.URL.revokeObjectURL(url)
            document.body.removeChild(a)
        } catch (e) {
            console.error('downloadReportPdf error:', e)
            throw e
        }
    }

    /** Download a Contract Compliance report (or full workbook) as XLSX. */
    async downloadContractReportXlsx (tenderId: string, report: string, month: string): Promise<void> {
        const qs = new URLSearchParams({ report, month })
        const res = await fetch(`${API_URL}/api/reports/contract/${tenderId}/export?${qs.toString()}`, {
            headers: await this.getAuthHeaders()
        })
        if (!res.ok) {
            const err = await res.json().catch(() => ({})) as { error?: string }
            throw new Error(err.error || 'Failed to export report')
        }
        const blob = await res.blob()
        const cd = res.headers.get('Content-Disposition')
        let name = `${report}-${month}.xlsx`
        if (cd) {
            const m = /filename="([^"]+)"/.exec(cd)
            if (m) name = m[1]
        }
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = name
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
    }

    async exportReport(params: {
        type: 'station-comparison' | 'trends' | 'compliance'
        format: 'csv' | 'json'
        tender_id?: string
        station_code?: string
        date_from?: string
        date_to?: string
        granularity?: 'daily' | 'weekly' | 'monthly'
    }): Promise<void> {
        try {
            const queryParams = new URLSearchParams()
            queryParams.append('type', params.type)
            queryParams.append('format', params.format)
            if (params.tender_id) queryParams.append('tender_id', params.tender_id)
            if (params.station_code) queryParams.append('station_code', params.station_code)
            if (params.date_from) queryParams.append('date_from', params.date_from)
            if (params.date_to) queryParams.append('date_to', params.date_to)
            if (params.granularity) queryParams.append('granularity', params.granularity)

            const res = await fetch(`${API_URL}/api/reports/export?${queryParams.toString()}`, {
                headers: await this.getAuthHeaders()
            })

            if (res.ok) {
                const blob = await res.blob()
                const url = window.URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = url
                const ext = params.format === 'csv' ? 'csv' : 'json'
                a.download = `report-${params.type}-${new Date().toISOString().split('T')[0]}.${ext}`
                document.body.appendChild(a)
                a.click()
                window.URL.revokeObjectURL(url)
                document.body.removeChild(a)
            } else {
                throw new Error('Failed to export report')
            }
        } catch (e: any) {
            console.error('Export report error:', e)
            throw e
        }
    }

    // ============================================
    // CHI DEPLOYMENT API
    // ============================================

    async getDeployments(params: { tender_id?: string; station_code?: string; date?: string; date_from?: string; date_to?: string; shift?: string; status?: string; limit?: number; offset?: number }) {
        const q = new URLSearchParams()
        if (params.tender_id) q.append('tender_id', params.tender_id)
        if (params.station_code) q.append('station_code', params.station_code)
        if (params.date) q.append('date', params.date)
        if (params.date_from) q.append('date_from', params.date_from)
        if (params.date_to) q.append('date_to', params.date_to)
        if (params.shift) q.append('shift', params.shift)
        if (params.status) q.append('status', params.status)
        if (params.limit != null) q.append('limit', String(params.limit))
        if (params.offset != null) q.append('offset', String(params.offset))
        const headers = await this.getAuthHeaders()
        const res = await fetch(`${API_URL}/api/chi-deployment?${q.toString()}`, { headers })
        const json = await res.json()
        if (!res.ok || json.success === false) {
            throw new Error(json.error || 'Failed to fetch deployments')
        }
        return { data: json.data ?? [], total: json.total ?? 0 }
    }

    async getDeployment(id: string) {
        try {
            const headers = await this.getAuthHeaders()
            const res = await fetch(`${API_URL}/api/chi-deployment/${id}`, { headers })
            const json = await res.json()
            return json.success ? json.data : null
        } catch (e) {
            console.error('getDeployment error:', e)
            return null
        }
    }

    async saveDeployment(data: unknown) {
        const headers = await this.getAuthHeaders()
        const res = await fetch(`${API_URL}/api/chi-deployment`, { method: 'POST', headers, body: JSON.stringify(data) })
        const json = await res.json()
        if (!res.ok) throw new Error(json.error || 'Failed to save deployment')
        return json.data
    }

    async updateDeploymentItems(id: string, items: unknown) {
        const headers = await this.getAuthHeaders()
        const res = await fetch(`${API_URL}/api/chi-deployment/${id}/items`, { method: 'PUT', headers, body: JSON.stringify({ items }) })
        const json = await res.json()
        if (!res.ok) throw new Error(json.error || 'Failed to update deployment items')
        return json.data ?? []
    }

    async submitDeployment(id: string, notes?: string) {
        const headers = await this.getAuthHeaders()
        const res = await fetch(`${API_URL}/api/chi-deployment/${id}/submit`, { method: 'POST', headers, body: JSON.stringify({ notes }) })
        const json = await res.json()
        if (!res.ok) throw new Error(json.error || 'Failed to submit deployment')
        return json.data
    }

    async getDeploymentSummary(params: { tender_id?: string; station_code?: string }) {
        try {
            const q = new URLSearchParams()
            if (params.tender_id) q.append('tender_id', params.tender_id)
            if (params.station_code) q.append('station_code', params.station_code)
            const headers = await this.getAuthHeaders()
            const res = await fetch(`${API_URL}/api/chi-deployment/summary?${q.toString()}`, { headers })
            const json = await res.json()
            return json.success ? json.data : null
        } catch (e) {
            console.error('getDeploymentSummary error:', e)
            return null
        }
    }

    async getConsumptionReport(params: { station_code: string; tender_id: string; month: string }) {
        const q = new URLSearchParams({ station_code: params.station_code, tender_id: params.tender_id, month: params.month })
        const headers = await this.getAuthHeaders()
        const res = await fetch(`${API_URL}/api/chi-deployment/consumption-report?${q.toString()}`, { headers })
        const json = await res.json()
        if (!res.ok || json.success === false) {
            throw new Error(json.error || 'Failed to load consumption report')
        }
        return { data: json.data ?? [], meta: json.meta }
    }

    async getSupplyReceipts(params: { tender_id?: string; station_code?: string; status?: string; limit?: number }) {
        const q = new URLSearchParams()
        if (params.tender_id) q.append('tender_id', params.tender_id)
        if (params.station_code) q.append('station_code', params.station_code)
        if (params.status) q.append('status', params.status)
        if (params.limit != null) q.append('limit', String(params.limit))
        const headers = await this.getAuthHeaders()
        const res = await fetch(`${API_URL}/api/supply-receipts?${q.toString()}`, { headers })
        const json = await res.json()
        if (!res.ok) throw new Error(json.error || 'Failed to fetch receipts')
        return { data: json.data ?? [], total: json.total ?? 0 }
    }

    async getSupplyReceiptPeriods(tenderId: string, stationCode: string) {
        const q = new URLSearchParams({ tender_id: tenderId, station_code: stationCode })
        const headers = await this.getAuthHeaders()
        const res = await fetch(`${API_URL}/api/supply-receipts/periods?${q.toString()}`, { headers })
        const json = await res.json()
        if (!res.ok) throw new Error(json.error || 'Failed to fetch periods')
        return json.data ?? []
    }

    async getSupplyReceipt(id: string) {
        const headers = await this.getAuthHeaders()
        const res = await fetch(`${API_URL}/api/supply-receipts/${id}`, { headers })
        const json = await res.json()
        if (!res.ok) throw new Error(json.error || 'Failed to fetch receipt')
        return json.data
    }

    async createSupplyReceipt(data: {
        tender_id: string
        station_code: string
        period_start: string
        period_end: string
        cadence: 'weekly' | 'biweekly' | 'monthly'
        notes?: string
    }) {
        const headers = await this.getAuthHeaders()
        const res = await fetch(`${API_URL}/api/supply-receipts`, { method: 'POST', headers, body: JSON.stringify(data) })
        const json = await res.json()
        if (!res.ok) throw new Error(json.error || 'Failed to create receipt')
        return json.data
    }

    async updateSupplyReceiptItems(id: string, payload: { items: unknown[]; notes?: string; photos?: string[] }) {
        const headers = await this.getAuthHeaders()
        const res = await fetch(`${API_URL}/api/supply-receipts/${id}/items`, { method: 'PUT', headers, body: JSON.stringify(payload) })
        const json = await res.json()
        if (!res.ok) throw new Error(json.error || 'Failed to save receipt items')
        return json.data ?? []
    }

    async submitSupplyReceipt(id: string, payload?: { notes?: string; photos?: string[] }) {
        const headers = await this.getAuthHeaders()
        const res = await fetch(`${API_URL}/api/supply-receipts/${id}/submit`, { method: 'POST', headers, body: JSON.stringify(payload ?? {}) })
        const json = await res.json()
        if (!res.ok) throw new Error(json.error || 'Failed to submit receipt')
        return json.data
    }

    async reopenSupplyReceipt(id: string, payload?: { reason?: string }) {
        const headers = await this.getAuthHeaders()
        const res = await fetch(`${API_URL}/api/supply-receipts/${id}/reopen`, { method: 'POST', headers, body: JSON.stringify(payload ?? {}) })
        const json = await res.json()
        if (!res.ok) throw new Error(json.error || 'Failed to reopen receipt')
        return json.data
    }

    async getSupplyInventory(tenderId: string, stationCode: string) {
        const q = new URLSearchParams({ tender_id: tenderId, station_code: stationCode })
        const headers = await this.getAuthHeaders()
        const res = await fetch(`${API_URL}/api/supply-receipts/inventory?${q.toString()}`, { headers })
        const json = await res.json()
        if (!res.ok) throw new Error(json.error || 'Failed to fetch inventory')
        return json.data ?? []
    }
}

const _api = new ApiService()

if (config.useMockApi) {
    const { getInitialMockTendersAndSubmissions } = await import('./api-mock-helpers')
    const { tenders, submissions } = getInitialMockTendersAndSubmissions()
    _api.initMockStoreForDev(tenders, submissions)
}

export const api = _api
